// ---------------------------------------------------------------------------
// Painel do Plen (cockpit) — a pagina do administrador. (Etapa C, v129; cockpit v139)
//
// Duas vistas, um nivel so':
//   INICIO   — responde "o piloto esta' vivo?" e "quem precisa de mim hoje?": bloco escuro
//              com a conclusao, 4 numeros, a carteira de professores (situacao, motivo,
//              proximo passo, WhatsApp), a faixa do app e o rodape "Como contamos".
//              Nunca inclui professor de teste; sem abas, sem periodo.
//   DETALHES — "← Inicio" + 8 assuntos (Professores, Pessoas, Crescimento, Acessos e
//              aparelhos, Telas e botoes, Onde desistem, Erros do app, Promocoes e premios).
//              Periodo e "Incluir contas de teste" so' aqui, onde valem.
//
// Autocontido de proposito: estilos inline + um <style> com prefixo pc-, graficos em SVG
// proprio (sem biblioteca), cores da marca. Serve de 375 px (Configuracoes -> Painel, no
// celular) a tela larga (/painel, no computador). Quem decide o acesso e' o BANCO (so' o
// e-mail do administrador passa); abrir a tela sem ser admin so' mostra o aviso.
//
// Funcoes do banco usadas (nenhuma nova nesta versao):
//   central_uso(p_dias, p_incluir_internos)    central_uso_pessoa(p_user, p_limite)
//   marcar_conta_interna(p_user, p_interna)    metricas_piloto()  (alunos, aulas e etapa)
//   tabela recompensas (premios a pagar)       painel_professores() — FASE 2, opcional:
//   se nao existir no banco, o painel segue sem ela e sem aviso.
// Porcentagens chegam de 0 a 100. Dinheiro "R$ 1.700" (redondo) ou "R$ 1.700,50".
// Os nomes legiveis de telas, funis, janelas e acoes vem dos catalogos de src/uso.js;
// se algum faltar, o codigo cru vira texto ("whatsapp abrir").
// ---------------------------------------------------------------------------
import React from 'react';
import * as CATALOGO from './uso.js';

const CAT = CATALOGO || {};
const TELAS = CAT.TELAS_CONHECIDAS || {};
const FLUXOS = CAT.FLUXOS || {};
const ACOES = CAT.ACOES_CONHECIDAS || {};
const JANELAS = CAT.JANELAS_CONHECIDAS || {};
const TRAVAS = CAT.VALIDACOES_SOLTAS || {};

// ---- Cores e fontes (marca Plen) ---------------------------------------------
// Professor = azul da marca; aluno = laranja queimado (passa no teste de daltonismo contra
// o azul). Verde/ambar/vermelho ficam SO' para estado (ativa, esfriando, inativa/erro).
const C = {
  azul: '#2F6BFF', azulEsc: '#1C43AE', azulClaro: '#E7EDFF',
  tinta: '#16181D', texto: '#3D424C', apoio: '#6E7480', dica: '#9AA0AB',
  borda: '#E2E4E9', linha: '#EEF0F3', fundo: '#F7F8FA', branco: '#FFFFFF', seg: '#EEF0F4',
  verde: '#0E9F6E', verdeEsc: '#0A6F4D', verdeClaro: '#DCF5EC',
  ambar: '#D97706', ambarEsc: '#B45309', ambarClaro: '#FEF3C7',
  verm: '#DC2626', vermEsc: '#B91C1C', vermClaro: '#FEE2E2',
  aluno: '#D2691E', alunoEsc: '#9A4A12', alunoClaro: '#FBEBDD',
  cinzaSerie: '#7A808A', visit: '#A0A6B0', eixo: '#C9CDD4',
};
const F_TIT = "'Sora', 'Manrope', system-ui, sans-serif";
const F_CORPO = "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ---- Numeros, datas, textos --------------------------------------------------
const num = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const arr = v => (Array.isArray(v) ? v : []);
const soma = (l, k) => arr(l).reduce((a, x) => a + num(x && x[k]), 0);
const fmtN = v => Math.round(num(v)).toLocaleString('pt-BR');
const fmtDec = (v, c = 1) => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: c });
// valor redondo sem ",00"; com centavos, sempre 2 casas ("R$ 1.700" e "R$ 1.700,50").
// O espaco depois de "R$" nao quebra (o "R$" nunca fica sozinho no fim da linha); fmtRq e' a
// versao que pode quebrar ali, para celula estreita e o valor grande do cartao de numero.
const fmtR = v => { const n = Math.round(num(v) * 100) / 100;
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }); };
const fmtRq = v => fmtR(v).replace(' ', ' ');
const fmtPct = v => { const n = num(v); return (n > 0 && n < 10 ? fmtDec(n, 1) : fmtN(n)) + '%'; };
const sinal = v => (num(v) > 0 ? '+' : num(v) < 0 ? '−' : '') + fmtN(Math.abs(num(v)));
const plural = (n, um, varios) => fmtN(n) + ' ' + (Math.round(num(n)) === 1 ? um : varios);
const nOuTraco = v => (v == null ? '—' : fmtN(v));   // "—" so' para dado que nao existe

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const FUSO = 'America/Sao_Paulo';

// 'AAAA-MM-DD' (dia/semana do banco) sem passar por Date — fuso nao mexe no dia.
function partesData(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? { a: +m[1], m: +m[2], d: +m[3] } : null;
}
const dd = n => String(n).padStart(2, '0');
const fmtDiaMes = s => { const p = partesData(s); return p ? dd(p.d) + '/' + dd(p.m) : '—'; };
const fmtDiaMesAno = s => { const p = partesData(s); return p ? dd(p.d) + '/' + dd(p.m) + '/' + String(p.a).slice(2) : '—'; };
const fmtSemana = s => 'Semana de ' + fmtDiaMesAno(s);
const fmtDiaLongo = s => {
  const p = partesData(s); if (!p) return '—';
  const sem = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][new Date(Date.UTC(p.a, p.m - 1, p.d)).getUTCDay()];
  return sem + ', ' + dd(p.d) + '/' + dd(p.m);
};
const fmtMes = s => { const m = /^(\d{4})-(\d{2})/.exec(String(s || '')); return m ? MESES[+m[2] - 1] + '/' + m[1].slice(2) : '—'; };
const fmtMesLongo = s => { const m = /^(\d{4})-(\d{2})/.exec(String(s || '')); return m ? MESES_LONGOS[+m[2] - 1] + ' de ' + m[1] : '—'; };

function dataValida(iso) { if (!iso) return null; const d = new Date(iso); return isNaN(d.getTime()) ? null : d; }
function chaveDiaSP(d) {
  try { const k = d.toLocaleDateString('en-CA', { timeZone: FUSO }); if (/^\d{4}-\d{2}-\d{2}$/.test(k)) return k; } catch (e) {}
  return d.toISOString().slice(0, 10);
}
function diasEntre(k1, k2) {
  const a = partesData(k1), b = partesData(k2);
  if (!a || !b) return 0;
  return Math.round((Date.UTC(b.a, b.m - 1, b.d) - Date.UTC(a.a, a.m - 1, a.d)) / 86400000);
}
function somaDias(k, n) {
  const p = partesData(k); if (!p) return k;
  return new Date(Date.UTC(p.a, p.m - 1, p.d) + n * 86400000).toISOString().slice(0, 10);
}
function diasAtras(iso) {
  const d = dataValida(iso); if (!d) return null;
  return Math.max(0, diasEntre(chaveDiaSP(d), chaveDiaSP(new Date())));
}
function quando(iso) {
  const n = diasAtras(iso);
  if (n == null) return 'nunca';
  if (n === 0) return 'hoje';
  if (n === 1) return 'ontem';
  if (n < 45) return 'há ' + n + ' dias';
  const m = Math.round(n / 30);
  if (m < 12) return m === 1 ? 'há 1 mês' : 'há ' + m + ' meses';
  return 'há mais de 1 ano';
}
function quandoCurto(iso) {
  const d = dataValida(iso); if (!d) return '—';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 1) return 'agora';
  if (min < 60) return 'há ' + min + ' min';
  const h = Math.round(min / 60);
  if (h < 24) return 'há ' + h + ' h';
  return quando(iso);
}
function fmtData(iso) {
  const d = dataValida(iso); if (!d) return '—';
  try { return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: FUSO }); } catch (e) { return fmtDiaMesAno(d.toISOString()); }
}
function fmtHora(iso) {
  const d = dataValida(iso); if (!d) return '—';
  try { return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO }); } catch (e) { return d.toISOString().slice(11, 16); }
}
function fmtDataHora(iso) {
  const d = dataValida(iso); if (!d) return '—';
  return fmtData(iso) + ' às ' + fmtHora(iso);
}
function fmtSeg(s) {
  s = Math.round(num(s));
  if (s < 60) return s + ' s';
  const m = Math.floor(s / 60), r = s % 60;
  if (m < 10 && r) return m + ' min ' + r + ' s';
  if (m < 60) return m + ' min';
  return Math.floor(m / 60) + ' h ' + (m % 60) + ' min';
}
function fmtMin(m) {
  m = num(m);
  if (m > 0 && m < 1) return 'menos de 1 min';
  if (m < 60) return fmtDec(m, m < 10 ? 1 : 0) + ' min';
  return fmtDec(m / 60, 1) + ' h';
}
const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ---- Nomes legiveis ----------------------------------------------------------
function bonito(c) {
  const s = String(c == null ? '' : c).replace(/^(acao|erro|aviso|fluxo|tela|janela|push|sessao|app)\./, '').replace(/[._]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}
const rotTela = c => TELAS[c] || bonito(c);
const rotAcao = c => { const k = String(c || '').replace(/^acao\./, ''); return ACOES[k] || bonito(k); };
const rotJanela = c => JANELAS[c] || bonito(c);
const rotFluxo = c => (FLUXOS[c] && FLUXOS[c].rotulo) || TRAVAS[c] || bonito(c);
const rotPasso = (f, p) => (FLUXOS[f] && FLUXOS[f].passos && FLUXOS[f].passos[p]) || ('Passo ' + p);
// Motivos de recusa e resultados gravados pelo app (uso.bloqueio / barrar / fim): os mais comuns
// em portugues de gente; o resto cai no codigo legivel ("sem acordo")
const MOTIVOS = {
  campo_vazio_text: 'deixou um campo em branco', campo_vazio_password: 'deixou a senha em branco',
  campo_vazio_email: 'deixou o e-mail em branco', campo_vazio_tel: 'deixou o telefone em branco',
  email_formato: 'e-mail escrito errado', email_ja_existe: 'e-mail que já tem conta', senha_errada: 'senha errada',
  telefone_ja_ativo: 'telefone que já tem conta', falha_criar: 'não conseguiu salvar', falha_salvar: 'não conseguiu salvar',
  sem_horario: 'não escolheu o horário', sem_hora: 'não escolheu o horário', sem_data: 'não escolheu a data',
  sem_data_hora: 'não escolheu data e hora', data_invalida: 'data inválida', data_futura: 'data no futuro',
  sem_motivo: 'não escolheu o motivo', sem_meio: 'não escolheu como foi pago', sem_nome: 'sem nome', outro: 'outro motivo',
};
const rotMotivo = m => {
  const k = String(m || 'outro');
  if (MOTIVOS[k]) return MOTIVOS[k];
  if (/^campo_vazio_/.test(k)) return MOTIVOS.campo_vazio_text;
  return bonito(k).toLowerCase();
};

// registro_acoes (o "livro" do servidor): entidade.verbo
const ACOES_SERVIDOR = {
  'aula.criada': 'Aula criada', 'aula.apagada': 'Aula apagada', 'aula.status': 'Aula marcada (feita, falta, cancelada)',
  'aula.remarcada': 'Aula remarcada', 'aula.desfecho_prof': 'Professor registrou como foi a aula',
  'aula.confirmada_aluno': 'Aluno confirmou presença', 'aula.falta_aluno': 'Aluno avisou falta',
  'aula.consome_credito': 'Mudou se a aula gasta crédito', 'aula.contrato': 'Aula trocou de acordo',
  'aula.professor': 'Aula trocou de professor', 'aula.alterada': 'Aula alterada',
  'pagamento.criado': 'Cobrança criada', 'pagamento.apagado': 'Cobrança apagada',
  'pagamento.status': 'Pagamento marcado (pago ou pendente)', 'pagamento.valor': 'Valor da cobrança mudou',
  'pagamento.vencimento': 'Vencimento mudou', 'pagamento.declarado': 'Aluno avisou "já paguei"',
  'pagamento.data_pagamento': 'Data do pagamento mudou', 'pagamento.professor': 'Pagamento trocou de professor',
  'pagamento.alterado': 'Pagamento alterado',
  'contrato.criado': 'Acordo criado', 'contrato.apagado': 'Acordo apagado', 'contrato.preco': 'Preço do acordo mudou',
  'contrato.reajuste': 'Reajuste proposto', 'contrato.aceite': 'Aluno aceitou o acordo',
  'contrato.status': 'Acordo encerrado ou reativado', 'contrato.professor': 'Acordo trocou de professor',
  'contrato.alterado': 'Acordo alterado',
};
const rotServidor = c => ACOES_SERVIDOR[c] || bonito(c);

const PAPEL = {
  professor: { r: 'Professor', bg: C.azulClaro, tx: C.azulEsc },
  aluno: { r: 'Aluno', bg: C.alunoClaro, tx: C.alunoEsc },
  admin: { r: 'Admin', bg: '#E9EBEF', tx: C.tinta },
  visitante: { r: 'Sem papel', bg: '#F0F1F4', tx: C.apoio },
};
const STATUS = {
  nova: { r: 'Nova', rp: 'Novas', cor: C.azul, bg: C.azulClaro, tx: C.azulEsc, dica: 'conta criada há até 7 dias' },
  ativa: { r: 'Ativa', rp: 'Ativas', cor: C.verde, bg: C.verdeClaro, tx: C.verdeEsc, dica: 'usou nos últimos 7 dias' },
  esfriando: { r: 'Esfriando', rp: 'Esfriando', cor: C.ambar, bg: C.ambarClaro, tx: C.ambarEsc, dica: 'última vez entre 8 e 30 dias atrás' },
  inativa: { r: 'Inativa', rp: 'Inativas', cor: C.verm, bg: C.vermClaro, tx: C.vermEsc, dica: 'mais de 30 dias sem usar' },
  nunca_usou: { r: 'Nunca usou', rp: 'Nunca usaram', cor: C.visit, bg: '#F0F1F4', tx: C.apoio, dica: 'conta com mais de 7 dias que não voltou depois do dia do cadastro' },
};
const ORDEM_STATUS = ['nova', 'ativa', 'esfriando', 'inativa', 'nunca_usou'];
const CHAVE_POR_PAPEL = { nova: 'novas', ativa: 'ativas', esfriando: 'esfriando', inativa: 'inativas', nunca_usou: 'nunca_usou' };

const PLATAFORMA = { ios: 'iPhone (app da loja)', android: 'Android (app da loja)', web: 'Navegador', pwa: 'Atalho na tela inicial' };
const rotPlat = p => PLATAFORMA[p] || (p ? bonito(p) : '—');
const PLAT_CURTA = { ios: 'iPhone', android: 'Android', web: 'Navegador', pwa: 'Atalho' };

const TIPO_ERRO = {
  'erro.js': 'Falha no código', 'erro.promessa': 'Falha escondida (não travou a tela)', 'erro.rede': 'Falha de internet',
  'erro.tela_branca': 'Tela branca (o app quebrou)', 'erro.perfil_prof': 'Perfil do professor não carregou',
};
const rotErro = t => TIPO_ERRO[t] || bonito(t);

const numVersao = v => { const m = /(\d+)$/.exec(String(v || '')); return m ? +m[1] : -1; };
const curtaVersao = v => String(v || '—').replace(/^plen-/, '');

// Etapas do metricas_piloto (mesma ordem do painel do piloto)
const FALTA_ETAPA = ['Falta cadastrar o 1º aluno', 'Falta mandar o convite', 'Falta o aluno aceitar', 'Falta fechar a 1ª aula'];

// ---- Dados: contrato do JSON com defaults (nunca null onde o painel conta) ------
function normalizar(d) {
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = null; } }
  d = d && typeof d === 'object' ? d : {};
  const r = d.resumo || {};
  const pp = r.por_papel || {};
  const ct = d.contas || {};
  const cpp = ct.por_papel || {};
  const cr = d.crescimento || {};
  return {
    gerado_em: d.gerado_em || null,
    dias: num(d.dias) || null,
    incluiu_internos: !!d.incluiu_internos,
    resumo: Object.assign({}, r, {
      por_papel: { professor: pp.professor || {}, aluno: pp.aluno || {} },
      por_plataforma: arr(r.por_plataforma), por_versao: arr(r.por_versao),
    }),
    por_dia: arr(d.por_dia), telas: arr(d.telas), janelas: arr(d.janelas), acoes: arr(d.acoes),
    acoes_servidor: arr(d.acoes_servidor), funis: arr(d.funis), cadastro: d.cadastro || {},
    pessoas: arr(d.pessoas), sumindo: arr(d.sumindo), retencao: arr(d.retencao), erros: arr(d.erros),
    contas: Object.assign({}, ct, { por_papel: { professor: cpp.professor || {}, aluno: cpp.aluno || {} } }),
    crescimento: {
      semanas: arr(cr.semanas), meses: arr(cr.meses),
      dau_mau_pct: num(cr.dau_mau_pct), crescimento_liquido_30d: num(cr.crescimento_liquido_30d),
      dias_medidos: cr.dias_medidos == null ? null : num(cr.dias_medidos),
    },
  };
}
function normalizarPessoa(d) {
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = null; } }
  if (Array.isArray(d)) return { cab: {}, eventos: d };
  d = d && typeof d === 'object' ? d : {};
  const cab = d.cabecalho || d.pessoa || d.cab || d;
  const eventos = arr(d.eventos || d.linha_do_tempo || d.linha || d.timeline || d.itens);
  return { cab, eventos };
}
function classificarErro(e) {
  const msg = String((e && (e.message || e.details || e.hint)) || e || '');
  const code = e && e.code;
  if (code === 'PGRST202' || code === '42883' || /could not find the function|schema cache|function .* does not exist/i.test(msg)) return { tipo: 'ausente', msg };
  if (code === '42501' || /acesso restrito|administrador|permission denied/i.test(msg)) return { tipo: 'negado', msg };
  if (/Failed to fetch|NetworkError|Load failed|network/i.test(msg)) return { tipo: 'rede', msg };
  return { tipo: 'outro', msg };
}
// "Versão mais nova" = a maior entre as usadas por alguém COM CONTA (versão que só aparece sem
// login pode ter sido inventada por quem chama o banco de fora).
function versaoMaisRecente(d) {
  let v = null;
  d.resumo.por_versao.forEach(x => { if (x && x.versao && num(x.usuarios) > 0 && numVersao(x.versao) > numVersao(v)) v = x.versao; });
  return v;
}
// Versao "no ar" do painel: a do pacote que abriu o painel (VERSAO_APP) ou, se alguem com conta
// ja' usa uma mais nova (o pacote em cache no celular pode estar atrasado), essa.
function versaoDoPainel(d, versaoNoAr) {
  const vMax = d ? versaoMaisRecente(d) : null;
  if (!versaoNoAr) return vMax;
  return vMax && numVersao(vMax) > numVersao(versaoNoAr) ? vMax : versaoNoAr;
}
// JSON que pode chegar como texto (algumas funcoes do banco devolvem json, outras text)
function lerJson(v) {
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch (e) { v = null; } }
  return v && typeof v === 'object' ? v : null;
}
// painel_professores (fase 2): so' entra quando a funcao existe e respondeu; qualquer erro
// (inclusive "funcao ausente", o caso normal da fase 1) vira null, em silencio.
function lerExtra(r) {
  if (!r || r.error) return null;
  const x = lerJson(r.data);
  return x && Array.isArray(x.professores) ? x : null;
}

// ---- Carteira de professores (Inicio e Detalhes › Professores) -----------------------
// Uma linha por professor REAL: metricas_piloto (alunos, aulas, etapa) casado com a lista de
// pessoas do central_uso (ultimo uso, status) pelo e-mail — ou pelo user_id, quando a fase 2
// (painel_professores) existe. A lista de pessoas ja' vem sem as contas de teste: professor do
// piloto que nao casa com ninguem e' pulado (so' quando a lista e' completa, menos de 500).
// Do metricas_piloto NUNCA se usa totais, funis, ultimo_uso, ultimo_acesso nem dias_parado: os
// totais e os funis contam professor de teste e o ultimo_uso conta aula criada pelo robo.
const ETAPA = ['Só criou a conta', 'Cadastrou alunos', 'Convidou alunos', 'Aluno no app', 'Registrou aula', 'Registrou pagamento'];
const SITUACAO = {
  parado: { r: 'Parado', bg: C.vermClaro, tx: C.vermEsc, cor: C.verm },
  ajuda: { r: 'Precisa de ajuda', bg: C.ambarClaro, tx: C.ambarEsc, cor: C.ambar },
  comecando: { r: 'Começando', bg: '#F0F1F4', tx: C.apoio, cor: C.dica },
  emdia: { r: 'Em dia', bg: C.verdeClaro, tx: C.verdeEsc, cor: C.verde },
  // linha parcial (sem alunos, aulas e etapa): nunca vira "Em dia" — nao da' para afirmar
  semdado: { r: 'Sem dados agora', bg: '#F0F1F4', tx: C.apoio, cor: C.dica },
};
const ehUrgente = l => l.sit.k === 'parado' || l.sit.k === 'ajuda';
const primeiroNome = n => String(n || '').trim().split(/\s+/)[0] || 'professor';
const minuscula1 = s => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '');
const MSG = {
  comecar: p => 'Oi, ' + p + '! Aqui é o Marcus, do Plen. Vi que você criou sua conta — quer que eu te ajude a cadastrar o primeiro aluno? Leva 5 minutos.',
  travado: (p, etapa) => 'Oi, ' + p + '! Aqui é o Marcus, do Plen. Vi que ainda ' + minuscula1(FALTA_ETAPA[etapa] || 'não começou') + '. Posso te ajudar com isso?',
  sumiu: p => 'Oi, ' + p + '! Aqui é o Marcus, do Plen. Está tudo certo com o app? Tem algo atrapalhando?',
  aulas: p => 'Oi, ' + p + '! Aqui é o Marcus, do Plen. Vi que não entrou aula nova no app — está tudo certo?',
  geral: p => 'Oi, ' + p + '! Aqui é o Marcus, do Plen.',
};
function mensagemDe(l) {
  const p = primeiroNome(l.nome);
  return l.sit.msg === 'travado' ? MSG.travado(p, l.etapa) : (MSG[l.sit.msg] || MSG.geral)(p);
}
// "Rafael", "Rafael e Ana", "Rafael, Ana e Rui"; mais que `max` nomes: devolve `contagem`
function nomesOuContagem(lista, max = 3, contagem = null) {
  const l = arr(lista).filter(Boolean);
  if (!l.length || l.length > max) return contagem;
  return l.length === 1 ? l[0] : l.slice(0, -1).join(', ') + ' e ' + l[l.length - 1];
}
// WhatsApp: com telefone (fase 2) abre a conversa da pessoa; sem, abre o WhatsApp para escolher
function linkWhats(tel, msg) {
  let f = String(tel || '').replace(/\D/g, '');
  if (f.length === 10 || f.length === 11) f = '55' + f;
  else if (!((f.length === 12 || f.length === 13) && f.indexOf('55') === 0)) f = '';
  return 'https://wa.me/' + f + '?text=' + encodeURIComponent(msg);
}

// Situacao do professor: a primeira regra que bater vale (especificacao v2, 3.3).
function saudeDe(l) {
  const e = l.etapa;
  const r = (k, motivo, passo, msg, cinza) => ({ k, motivo, passo, msg, cinza: !!cinza });
  if (l.status === 'nunca_usou') return r('parado', 'Criou a conta ' + quando(l.criado_em) + ' e não voltou', 'Ligar e oferecer ajuda para começar', 'comecar');
  if (l.status === 'esfriando' || l.status === 'inativa') {
    return r('parado', 'Sem usar há ' + plural(l.semUso, 'dia', 'dias') + (e !== null && e < 4 ? ' · parou em “' + ETAPA[e] + '”' : ''), 'Mandar um oi e perguntar o que aconteceu', 'sumiu');
  }
  if (l.sumindo) return r('ajuda', 'Usava bastante e sumiu há ' + plural(l.sumindo.dias_sumido, 'dia', 'dias'), 'Mandar um oi e perguntar se está tudo bem', 'sumiu');
  if (e !== null && e < 4 && l.idade >= 3) {
    if (e === 0) return r('ajuda', 'Ainda não cadastrou nenhum aluno', 'Ajudar a cadastrar o 1º aluno', 'travado');
    if (e === 1) return r('ajuda', 'Cadastrou ' + plural(l.alunos, 'aluno', 'alunos') + ', mas ainda não convidou', 'Lembrar de convidar os alunos para o app', 'travado');
    if (e === 2) return r('ajuda', plural(l.convites, 'convite', 'convites') + ' sem resposta', 'Ver se os alunos receberam o convite', 'travado');
    return r('ajuda', 'Tem aluno no app, mas nenhuma aula registrada', 'Mostrar como fechar a aula do dia', 'travado');
  }
  if (e !== null && e >= 4 && l.aulas30 === 0) return r('ajuda', 'Nenhuma aula registrada nos últimos 30 dias', 'Perguntar se já está dando aula pelo app', 'aulas');
  if (e !== null && e >= 4 && l.aulas7 === 0 && l.aulas30 > 0 && l.acordos > 0) {
    return r('ajuda', 'Nenhuma aula registrada em 7 dias (' + fmtN(l.aulas30) + ' em 30 dias)', 'Perguntar se as aulas pararam ou se só não marcou no app', 'aulas');
  }
  if ((e === null || e < 4) && l.idade < 3) {
    return r('comecando', 'Conta criada ' + quando(l.criado_em), e === null || e === 0 ? 'Dar boas-vindas e ajudar no 1º aluno' : 'Dar boas-vindas', 'comecar');
  }
  // linha parcial: as regras 4, 5 e 6 nao rodam e a regra 8 ("Em dia") seria um chute
  if (e === null) {
    return l.semPiloto
      ? r('semdado', 'Não deu para ler alunos, aulas e etapa agora', 'Tocar em Atualizar', 'geral', true)
      : r('semdado', 'Não achamos os alunos e as aulas dele', 'Ver se o e-mail do cadastro é o mesmo do login', 'geral', true);
  }
  if (e === 4) return r('emdia', '', 'Mostrar como registrar o pagamento', 'geral');
  if (e === 5 && l.noApp < l.alunos) return r('emdia', '', 'Nada urgente · ' + plural(l.alunos - l.noApp, 'aluno ainda fora', 'alunos ainda fora') + ' do app', 'geral', true);
  return r('emdia', '', 'Nada a fazer agora', 'geral', true);
}

// Fase 2: etapa 5 so' com pagamento de verdade (a migracao "ja' pago" nao conta) — mesma
// escada do metricas_piloto.sql
function etapaSemMigracao(pr, ex) {
  if (num(ex.aulas_total) > 0) return 4;
  if (num(pr.alunos_com_conta) > 0) return 3;
  if (num(pr.alunos_convidados) > 0) return 2;
  if (num(pr.alunos) > 0) return 1;
  return 0;
}
function linhaCarteira(pr, pes, ex, d, semPiloto) {
  const criado = (pr && pr.criado_em) || (pes && pes.conta_criada_em) || null;
  const dc = dataValida(criado);
  const idade = diasAtras(criado);
  let etapa = pr && pr.etapa != null ? num(pr.etapa) : null;
  if (pr && ex && etapa === 5 && num(ex.pagos_reais) === 0) etapa = etapaSemMigracao(pr, ex);
  const status = pes ? pes.status : null;
  const ultima = pes ? pes.ultima_atividade || null : null;
  const uid = pes ? pes.user_id || null : null;
  const l = {
    chave: uid || 'p:' + (pr ? pr.id : ''),
    profId: pr ? pr.id : null,
    user_id: uid,
    nome: (pes && pes.nome) || (pr && pr.nome) || 'Sem nome',
    email: (pes && pes.email) || (pr && pr.email) || '',
    interno: !!(pes && pes.interno),
    criado_em: criado,
    diaCriado: dc ? chaveDiaSP(dc) : null,
    idade: idade == null ? 0 : idade,
    etapa,
    alunos: pr ? num(pr.alunos) : null,
    convites: pr ? num(pr.alunos_convidados) : null,
    noApp: ex ? num(ex.alunos_no_app) : pr ? Math.min(num(pr.alunos_com_conta), num(pr.alunos)) : null,
    acordos: pr ? num(pr.contratos_ativos) : null,
    aulas7: ex ? num(ex.aulas_7d) : pr ? num(pr.aulas_7d) : null,
    aulas7antes: ex && ex.aulas_7d_antes != null ? num(ex.aulas_7d_antes) : null,
    aulas30: pr ? num(pr.aulas_30d) : null,
    pendente: pr ? num(pr.pendente) : null,
    recebidoTotal: ex ? num(ex.recebido_total) : pr ? num(pr.recebido_total) : null,
    recebidoMes: ex ? num(ex.recebido_mes) : null,
    fase2: !!ex,
    telefone: ex ? ex.telefone || null : null,
    ultima, status,
    dias30: pes ? num(pes.dias_ativos_30d) : null,
    plataforma: pes ? pes.plataforma : null,
    versao: pes ? pes.versao : null,
    semUso: status === 'nunca_usou' ? null : diasAtras(ultima),
    sumindo: uid ? arr(d && d.sumindo).find(s => s && s.user_id === uid) || null : null,
    semPiloto: !!semPiloto,
  };
  l.sit = saudeDe(l);
  return l;
}
function montarCarteira(piloto, d, extra) {
  const pessoas = d ? arr(d.pessoas) : [];
  const porEmail = {}, porUid = {}, extraPorId = {};
  pessoas.forEach(p => {
    if (!p) return;
    if (p.email) porEmail[String(p.email).toLowerCase()] = p;
    if (p.user_id) porUid[p.user_id] = p;
  });
  arr(extra && extra.professores).forEach(x => { if (x && x.prof_id) extraPorId[x.prof_id] = x; });
  const comInternos = !!(d && d.incluiu_internos);
  // lista vazia tambem filtra (todo professor e' pulado); com 500 ou mais a lista do SQL foi
  // cortada e filtrar esconderia professor real
  const filtrar = !comInternos && pessoas.length < 500;
  const linhas = [];
  const usados = {};
  const semPiloto = !(piloto && Array.isArray(piloto.professores));
  let escondidos = 0;
  arr(piloto && piloto.professores).forEach(pr => {
    if (!pr) return;
    const ex = extraPorId[pr.id] || null;
    const pes = (ex ? porUid[ex.user_id] : porEmail[String(pr.email || '').toLowerCase()]) || null;
    if ((ex && ex.interno && !comInternos) || (filtrar && !pes) || (pes && pes.papel === 'admin')) { escondidos++; return; }
    if (pes && pes.user_id) usados[pes.user_id] = true;
    linhas.push(linhaCarteira(pr, pes, ex, d));
  });
  // professor que esta' na lista de pessoas mas nao casou (ou o piloto nao respondeu): linha parcial
  pessoas.forEach(p => {
    if (p && p.papel === 'professor' && !(p.user_id && usados[p.user_id])) linhas.push(linhaCarteira(null, p, null, d, semPiloto));
  });
  return { linhas, escondidos };
}
// Grupos do Inicio: precisa de voce (parado, depois ajuda), comecando, em dia e, por ultimo,
// os sem dados (linha parcial)
function agrupar(linhas) {
  const urg = linhas.filter(ehUrgente).sort((a, b) =>
    (a.sit.k === 'parado' ? 0 : 1) - (b.sit.k === 'parado' ? 0 : 1)
    || num(b.semUso != null ? b.semUso : b.idade) - num(a.semUso != null ? a.semUso : a.idade)
    || (a.etapa === null ? 99 : a.etapa) - (b.etapa === null ? 99 : b.etapa));
  const com = linhas.filter(l => l.sit.k === 'comecando').sort((a, b) => String(b.criado_em || '').localeCompare(String(a.criado_em || '')));
  const ok = linhas.filter(l => l.sit.k === 'emdia').sort((a, b) =>
    num(b.aulas7) - num(a.aulas7)
    || String(b.ultima || '').localeCompare(String(a.ultima || ''))
    || String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  const sem = linhas.filter(l => l.sit.k === 'semdado').sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  return { urg, com, ok, sem };
}
// "Ultimo uso" de um professor: quem nunca voltou mostra "so' no cadastro" [C6]; conta nova
// (ate' 7 dias) cuja ultima atividade e' o proprio dia do cadastro, "so' no dia do cadastro"
function ultimoUsoTxt(l) {
  if (l.status === 'nunca_usou') return 'só no cadastro';
  const u = dataValida(l.ultima);
  if (!u) return '—';
  if (l.status === 'nova' && l.idade > 0 && l.diaCriado && chaveDiaSP(u) === l.diaCriado) return 'só no dia do cadastro';
  return quando(l.ultima);
}

// ---- Semanas de referencia (semana fechada × a anterior; a corrente a' parte) -----------
const diaDaSemanaUTC = k => { const p = partesData(k); return p ? new Date(Date.UTC(p.a, p.m - 1, p.d)).getUTCDay() : 0; };
const chaveSemana = s => String((s && s.semana) || '').slice(0, 10);
function rotuloSemana(seg) {
  const a = partesData(seg), b = partesData(somaDias(seg, 6));
  if (!a || !b) return '—';
  return a.m === b.m ? a.d + ' a ' + b.d + '/' + MESES[b.m - 1] : a.d + '/' + MESES[a.m - 1] + ' a ' + b.d + '/' + MESES[b.m - 1];
}
// "Hoje" vem do momento em que o banco montou os numeros (gerado_em), nao do relogio do aparelho:
// celular com a data errada ou numeros carregados ha' dias nao viram "primeira semana do piloto".
function semanasRef(semanas, geradoEm) {
  const l = arr(semanas);
  const hoje = chaveDiaSP(dataValida(geradoEm) || new Date());
  const segunda = somaDias(hoje, -((diaDaSemanaUTC(hoje) + 6) % 7));
  const n = l.length;
  let atual = n && chaveSemana(l[n - 1]) === segunda ? l[n - 1] : null;
  let iRef = -1;
  if (atual) iRef = n - 2;
  else if (n && chaveSemana(l[n - 1]) === somaDias(segunda, -7)) iRef = n - 1;
  // nem a desta segunda nem a anterior: vale o contrato do banco (a ultima linha e' sempre a
  // semana corrente, central_uso.sql:1195)
  else if (n) { atual = l[n - 1]; iRef = n - 2; }
  const ref = iRef >= 0 ? l[iRef] : null;
  const ant = iRef >= 1 ? l[iRef - 1] : null;
  const corte = ref ? iRef : atual ? n - 1 : n;
  return {
    atual, ref, ant,
    antesDaRef: l.slice(0, corte),
    ult8: l.slice(-8),
    rotuloRef: ref ? rotuloSemana(chaveSemana(ref)) : '',
  };
}
// Comparacao sempre em unidades, com o "antes" escrito (e em cinza na tela)
function compara(a, b, um, varios) {
  a = num(a); b = num(b);
  if (a > b) return '↑ ' + plural(a - b, um, varios) + ' a mais que na semana anterior';
  if (a < b) return '↓ ' + plural(b - a, um, varios) + ' a menos que na semana anterior';
  return a > 0 ? '= igual à semana anterior' : 'Nenhuma na semana anterior também';
}
function comparaCurto(a, b) {
  a = num(a); b = num(b);
  if (a > b) return fmtN(a - b) + ' a mais que na anterior';
  if (a < b) return fmtN(b - a) + ' a menos que na anterior';
  return 'igual à anterior';
}
const fmtSemanaCurta = s => fmtDiaMes(chaveSemana(s) || s);
// "qua, 14 de outubro" (dia de Brasilia)
function fmtHojeLongo() {
  const agora = new Date();
  const p = partesData(chaveDiaSP(agora));
  let sem = '';
  try { sem = agora.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: FUSO }).replace(/\./g, ''); } catch (e) {}
  if (!sem && p) sem = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][diaDaSemanaUTC(chaveDiaSP(agora))];
  return p ? sem + ', ' + p.d + ' de ' + MESES_LONGOS[p.m - 1] : '';
}

// Bloco escuro: titulo (a conclusao) + no maximo 2 frases, por regra (sem IA). Especificacao 4.1.
function estadoPlen({ linhas, sr, errosNoAr, v, premiosReais, premiosTodos }) {
  const urg = linhas.filter(ehUrgente);
  const profAbriram = linhas.filter(l => l.semUso !== null && l.semUso <= 6).length;
  const semDado = linhas.filter(l => l.sit.k === 'semdado').length;
  let titulo, bolinha, ver = false;
  const nenhum = linhas.length === 0;
  if (nenhum) { titulo = 'Ainda não há professores de verdade no Plen.'; bolinha = C.dica; }
  else if (urg.length) {
    const g = agrupar(urg).urg;
    const nomes = nomesOuContagem(g.map(l => primeiroNome(l.nome)), 3, null);
    titulo = nomes ? nomes + (g.length === 1 ? ' precisa' : ' precisam') + ' de você hoje.' : fmtN(g.length) + ' professores precisam de você hoje.';
    ver = !nomes;
    bolinha = urg.some(l => l.sit.k === 'parado') ? C.verm : C.ambar;
  } else if (num(sr.ref && sr.ref.aulas_dadas) === 0 && num(sr.atual && sr.atual.aulas_dadas) === 0 && profAbriram === 0) {
    titulo = 'Semana parada: nenhuma aula e nenhum professor abriu o app.'; bolinha = C.dica;
  } else if (semDado) {
    // sem alunos, aulas e etapa de alguem, "ninguem travado" nao foi conferido
    titulo = semDado === linhas.length ? 'Não deu para conferir os professores agora.' : 'Não deu para conferir todos os professores agora.';
    bolinha = C.dica;
  } else { titulo = 'Piloto andando: ninguém travado hoje.'; bolinha = C.verde; }

  // 1. avisos, na ordem: erro na versao no ar, premio, primeiro dinheiro, professor novo.
  // Vale 1 aviso + a frase de ritmo; a excecao e' erro + premio: os dois entram e o ritmo sai
  // (ele ja' esta' no cartao de aulas) — o premio a pagar nunca some do Inicio por causa do erro.
  const erroF = errosNoAr.length
    ? { t: 'O app deu erro na versão no ar (' + curtaVersao(v) + '): ' + plural(errosNoAr.length, 'tipo de erro', 'tipos de erro'), ir: 'erros', cor: C.verm }
    : null;
  let avisoF = null;
  if (!nenhum) {
    const somaGmv = arr(sr.antesDaRef).reduce((a, s) => a + num(s.gmv_recebido), 0);
    const gRef = num(sr.ref && sr.ref.gmv_recebido), gAtual = num(sr.atual && sr.atual.gmv_recebido);
    const novos = linhas.filter(l => {
      const desde = sr.ref ? chaveSemana(sr.ref) : sr.atual ? chaveSemana(sr.atual) : null;
      return desde && l.diaCriado && l.diaCriado >= desde;
    }).sort((a, b) => String(a.criado_em || '').localeCompare(String(b.criado_em || '')));
    if (premiosReais.length) {
      // o "ver" abre a lista "A pagar", que mostra todos os premios (inclusive de professor de teste)
      const aMais = Math.max(0, num(premiosTodos) - premiosReais.length);
      avisoF = { premio: true, ir: 'promocoes', t: plural(premiosReais.length, 'prêmio', 'prêmios') + ' para pagar (' + fmtR(soma(premiosReais, 'valor')) + ')'
        + (aMais ? '; a lista mostra mais ' + fmtN(aMais) + ' de professor de teste' : '') };
    } else if (somaGmv === 0 && gRef + gAtual > 0) {
      // a semana pode ter mais de um pagamento: o valor e' a soma, nao "o primeiro pagamento"
      avisoF = { t: 'Primeiros pagamentos registrados no Plen: ' + (gRef > 0 ? fmtR(gRef) + ', na semana passada.' : fmtR(gAtual) + ', esta semana.') };
    } else if (novos.length === 1) {
      avisoF = { t: 'Professor novo: ' + novos[0].nome + ' (entrou em ' + fmtDiaMes(novos[0].diaCriado) + ').' };
    } else if (novos.length > 1) {
      avisoF = { t: fmtN(novos.length) + ' professores novos: ' + novos.slice(0, 3).map(l => l.nome).join(', ') + (novos.length > 3 ? '…' : '.') };
    }
  }
  const frases = [];
  if (erroF && avisoF && avisoF.premio) return { titulo, bolinha, ver, frases: [erroF, avisoF] };
  if (erroF || avisoF) frases.push(erroF || avisoF);
  // 2. ritmo (sempre, menos quando ainda nao ha professor)
  if (!nenhum) {
    const { ref, ant, atual } = sr;
    if (ref) {
      const n = num(ref.aulas_dadas);
      const aulas = n === 0 ? 'nenhuma aula registrada' : plural(n, 'aula registrada', 'aulas registradas');
      frases.push({ t: 'Semana passada (' + sr.rotuloRef + '): ' + aulas + (ant ? ', ' + comparaCurto(n, ant.aulas_dadas) + '.' : ' — a primeira semana completa do piloto.') });
    } else {
      frases.push({ t: 'Primeira semana do piloto: ' + plural(num(atual && atual.aulas_dadas), 'aula registrada', 'aulas registradas') + ' até agora.' });
    }
  }
  return { titulo, bolinha, ver, frases: frases.slice(0, 2) };
}
// Validações que barraram gente FORA de um assistente (login, senha, reagendar...): no banco
// vêm como funil sem nenhuma passada (iniciados 0) e com bloqueios.
function travasSoltas(d) {
  const out = [];
  d.funis.forEach(f => {
    if (num(f.iniciados) > 0) return;
    arr(f.bloqueios).forEach(b => out.push({ fluxo: f.fluxo, motivo: b.motivo, n: num(b.n) }));
  });
  return out.sort((a, b) => b.n - a.n || String(a.fluxo).localeCompare(String(b.fluxo)));
}

// Funis em "degraus": [{chave, rotulo, n, seg, pararam}] — o ultimo e' "Concluiram".
const PASSOS_CADASTRO = [
  ['abriu_entrada', 'Abriu a tela de entrada'],
  ['abriu_cadastro', 'Abriu a tela de criar conta'],
  ['enviou_cadastro', 'Enviou os dados'],
  ['criou_conta', 'Conta criada'],
  ['escolheu_papel', 'Escolheu professor ou aluno'],
  ['chegou_no_app', 'Chegou no app'],
];
function degrausCadastro(cad) { return PASSOS_CADASTRO.map(([k, r]) => ({ chave: k, rotulo: r, n: num(cad && cad[k]) })); }
function degrausFluxo(f) {
  const passos = arr(f.passos).slice().sort((a, b) => num(a.passo) - num(b.passo));
  const saidas = {};
  arr(f.saidas).forEach(s => { const k = num(s.passo); saidas[k] = (saidas[k] || 0) + num(s.n); });
  const out = passos.map(p => ({
    chave: 'p' + p.passo, rotulo: rotPasso(f.fluxo, p.passo), n: num(p.chegaram),
    seg: p.segundos_medianos == null ? null : num(p.segundos_medianos), pararam: saidas[num(p.passo)] || 0,
  }));
  if (!out.length) out.push({ chave: 'ini', rotulo: 'Começaram', n: num(f.iniciados) });
  out.push({ chave: 'fim', rotulo: 'Concluíram', n: num(f.concluidos), fim: true });
  return out;
}
// Queda de menos de 10% de um degrau para o outro nao vira destaque (e' ruido, nao atrito),
// e com menos de 10 tentativas no degrau de antes tambem nao (amostra pequena demais).
const QUEDA_MIN_PCT = 10;
const QUEDA_MIN_N = 10;
function maiorQueda(degraus, desde = 1) {
  let iq = -1, q = 0;
  for (let i = Math.max(1, desde); i < degraus.length; i++) {
    const ant = degraus[i - 1].n;
    const p = ant - degraus[i].n;
    if (p > q && ant >= QUEDA_MIN_N && (p / ant) * 100 >= QUEDA_MIN_PCT) { q = p; iq = i; }
  }
  if (iq < 0) return null;
  const de = degraus[iq - 1];
  return { i: iq, perdeu: q, de, para: degraus[iq], pct: de.n ? (q / de.n) * 100 : 0 };
}
function topBloqueio(f) {
  let m = null;
  arr(f.bloqueios).forEach(b => { if (!m || num(b.n) > num(m.n)) m = b; });
  return m;
}
function quedasDosFunis(d) {
  const out = [];
  const qc = maiorQueda(degrausCadastro(d.cadastro), 2);
  if (qc) out.push(Object.assign({ chave: 'cad', fluxo: '_cadastro', rotulo: 'Entrada no app (por visita)' }, qc));
  d.funis.forEach(f => {
    const q = maiorQueda(degrausFluxo(f));
    if (q) out.push(Object.assign({ chave: f.fluxo, fluxo: f.fluxo, rotulo: rotFluxo(f.fluxo), bloqueio: topBloqueio(f) }, q));
  });
  return out.sort((a, b) => b.perdeu - a.perdeu || b.pct - a.pct);
}

// ---- Preferencias deste aparelho: so' {dias, secao} -------------------------------
// A vista nunca e' lembrada (sempre abre no Inicio) e "contas de teste" sempre comeca
// desligada. Chaves antigas (aba, internos) sao ignoradas. Sem armazenamento, tudo igual.
const K_PREFS = 'plen_central_prefs';
function lerPrefs() { try { const p = JSON.parse(localStorage.getItem(K_PREFS) || '{}'); return p && typeof p === 'object' ? p : {}; } catch (e) { return {}; } }
function gravarPrefs(p) { try { localStorage.setItem(K_PREFS, JSON.stringify({ dias: p.dias, secao: p.secao })); } catch (e) {} }

// ---- Estilo (um <style> so', prefixo pc-) --------------------------------------
const CSS = `
.pc-raiz{font-family:${F_CORPO};font-size:14px;line-height:1.45;color:${C.tinta};-webkit-font-smoothing:antialiased;min-width:0}
.pc-raiz *{box-sizing:border-box}
.pc-raiz button,.pc-raiz input,.pc-raiz select{font-family:inherit}
.pc-num{font-variant-numeric:tabular-nums}
.pc-cartoes{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
.pc-grade{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr));gap:14px;align-items:start}
.pc-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1.5px solid ${C.borda};background:#fff;border-radius:10px;padding:8px 14px;min-height:40px;font-size:13px;font-weight:700;color:${C.texto};cursor:pointer;white-space:nowrap}
.pc-btn:hover{border-color:#C9CDD4}
.pc-btn:disabled{opacity:.6;cursor:default}
.pc-btn.forte{background:${C.azul};border-color:${C.azul};color:#fff}
.pc-link{display:inline-flex;align-items:center;min-height:44px;border:0;background:transparent;padding:8px 0;color:${C.azul};font-weight:700;font-size:13px;cursor:pointer;text-align:left;text-decoration:none}
.pc-link.inl{display:inline;min-height:0;padding:12px 2px;margin:-12px 0;font-size:inherit;line-height:inherit}
.pc-link.claro{color:#9DB8FF}
.pc-seg{display:inline-flex;flex-wrap:wrap;max-width:100%;background:${C.seg};border-radius:12px;padding:3px;gap:2px}
.pc-seg button{border:0;background:transparent;padding:7px 12px;min-height:34px;border-radius:9px;font-size:13px;font-weight:700;color:${C.apoio};cursor:pointer;white-space:nowrap}
.pc-seg button.on{background:#fff;color:${C.tinta};box-shadow:0 1px 2px rgba(22,24,29,.08)}
.pc-chip{display:inline-flex;align-items:center;gap:6px;border:1.5px solid ${C.borda};background:#fff;border-radius:999px;padding:6px 12px;min-height:36px;font-size:12.5px;font-weight:700;color:${C.texto};cursor:pointer;white-space:nowrap}
.pc-chip.on{background:${C.tinta};border-color:${C.tinta};color:#fff}
.pc-linha-btn{display:block;width:100%;text-align:left;border:0;background:transparent;padding:10px 8px;margin:0 -8px;width:calc(100% + 16px);border-radius:10px;cursor:pointer;color:inherit;font-size:inherit}
.pc-linha-btn:hover{background:${C.fundo}}
.pc-raiz button:focus-visible,.pc-raiz input:focus-visible,.pc-raiz select:focus-visible,.pc-raiz [tabindex]:focus-visible{outline:2px solid ${C.azul};outline-offset:2px}
.pc-tabela{width:100%;border-collapse:collapse;font-size:12.5px}
.pc-tabela th{text-align:left;padding:8px 10px;font-size:11px;font-weight:800;color:${C.apoio};text-transform:uppercase;letter-spacing:.03em;background:${C.fundo};white-space:nowrap;position:sticky;top:0;z-index:1}
.pc-tabela td{padding:8px 10px;border-top:1px solid ${C.linha};color:${C.texto};vertical-align:top}
.pc-tabela td.d,.pc-tabela th.d{text-align:right}
.pc-rolagem{overflow-x:auto;-webkit-overflow-scrolling:touch}
.pc-busca{width:100%;border:1.5px solid ${C.borda};border-radius:12px;padding:10px 12px;font-size:15px;background:#fff;color:${C.tinta};outline:none;min-height:44px}
.pc-busca:focus{border-color:${C.azul}}
.pc-topo{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:8px 16px}
.pc-topo-esq{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0}
.pc-heroi{background:#16181D;color:#fff;border-radius:20px;padding:22px 26px}
.pc-heroi-cab{display:flex;justify-content:space-between;align-items:flex-start;gap:4px 16px}
.pc-heroi-tit{display:flex;align-items:flex-start;gap:10px;font-family:${F_TIT};font-size:22px;font-weight:700;line-height:1.3;letter-spacing:-0.01em;min-width:0}
.pc-heroi-data{font-size:13px;color:${C.dica};white-space:nowrap;padding-top:4px}
.pc-heroi-frases{list-style:none;margin:10px 0 0;padding:0;display:flex;flex-direction:column;gap:6px}
.pc-heroi-frase{display:flex;align-items:flex-start;gap:10px;font-size:15px;line-height:1.45;color:#E6E8EC}
.pc-bola{display:inline-block;border-radius:50%;flex:0 0 auto}
.pc-kpis{display:grid;gap:12px;grid-template-columns:minmax(0,1.4fr) repeat(3,minmax(0,1fr))}
.pc-kpi{display:flex;flex-direction:column;align-items:stretch;gap:3px;width:100%;min-width:0;text-align:left;background:#fff;border:1px solid ${C.borda};border-radius:16px;padding:14px 16px;cursor:pointer;color:${C.tinta};font-size:14px}
.pc-kpi:hover{border-color:#C9CDD4}
.pc-kpi-topo{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-start;gap:4px 6px}
.pc-kpi-rot{font-size:12px;font-weight:800;color:${C.apoio};line-height:1.35;min-width:0}
.pc-kpi-etq{background:#F0F1F4;color:${C.apoio};border-radius:999px;padding:1px 8px;font-size:11px;font-weight:700;white-space:nowrap;line-height:1.6}
.pc-kpi-meio{display:flex;justify-content:space-between;align-items:flex-end;gap:4px 10px;flex-wrap:wrap;margin-top:2px}
.pc-kpi-valor{font-family:${F_TIT};font-size:30px;font-weight:800;line-height:1.1;letter-spacing:-0.02em;color:${C.tinta};min-width:0;overflow-wrap:anywhere}
.pc-kpi-valor.longo{font-size:24px}
.pc-kpi-de{font-size:18px;font-weight:700;color:${C.apoio};letter-spacing:0;white-space:nowrap}
.pc-kpi-linha{display:block;font-size:12.5px;color:${C.apoio};line-height:1.4}
.pc-barrinhas{display:block;height:36px}
.pc-profs{display:grid;gap:12px;grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr))}
.pc-profs>.pc-grupo{grid-column:1/-1;margin-top:6px}
.pc-grupos{display:grid;gap:14px 16px;grid-template-columns:minmax(0,1fr);align-items:start}
.pc-grupo-col{display:flex;flex-direction:column;gap:8px;min-width:0}
a.pc-btn{text-decoration:none}
.pc-grupo{font-size:11px;font-weight:800;color:${C.apoio};text-transform:uppercase;letter-spacing:.05em}
.pc-prof{display:flex;flex-direction:column;gap:5px;background:#fff;border:1px solid ${C.borda};border-radius:14px;padding:12px 14px;min-width:0}
.pc-prof.clic{cursor:pointer}
.pc-prof.clic:hover{border-color:#C9CDD4}
.pc-prof-nome{display:block;border:0;background:transparent;padding:13px 0;margin:-13px 0;font-size:15px;font-weight:800;color:${C.tinta};text-align:left;cursor:pointer;min-width:0;line-height:1.3;overflow-wrap:anywhere}
.pc-contato{display:flex;flex-wrap:wrap;gap:8px;margin-top:4px}
.pc-btn.peq{min-height:44px;padding:8px 12px;font-size:12.5px}
.pc-chips-sec .pc-chip,.pc-links .pc-chip{min-height:44px}
.pc-links{display:flex;flex-wrap:wrap;gap:8px}
.pc-faixa{display:flex;align-items:center;gap:8px;width:100%;min-height:44px;text-align:left;background:#fff;border:1px solid ${C.borda};border-radius:16px;padding:10px 14px;cursor:pointer;font-size:13px;line-height:1.45}
.pc-faixa:hover{border-color:#C9CDD4}
.pc-sum{display:flex;align-items:center;gap:8px;min-height:44px;cursor:pointer;font-size:13px;font-weight:700;color:${C.texto}}
.pc-sum-seta{margin-left:auto;display:inline-flex;color:${C.dica}}
.pc-sum-seta svg{transition:transform .15s ease-out}
.pc-detalhes[open]>.pc-sum .pc-sum-seta svg{transform:rotate(180deg)}
.pc-detalhes{background:#fff;border:1px solid ${C.borda};border-radius:16px;padding:2px 16px}
.pc-detalhes[open]{padding-bottom:12px}
.pc-esq{background:#ECEEF2;border-radius:14px}
.pc-esq-heroi{min-height:132px}
.pc-esq-kpi{height:132px}
.pc-semanas th,.pc-semanas td{padding:7px 6px}
.pc-semanas th{white-space:normal;line-height:1.3}
.pc-semanas td:first-child{color:${C.tinta};font-weight:700;min-width:96px}
.pc-tab-prof th{white-space:normal;vertical-align:bottom}
.pc-raiz,#pc-professores,#pc-ficha{scroll-margin-top:12px}
@keyframes pc-gira{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:no-preference){.pc-atualizar:disabled svg{animation:pc-gira 1s linear infinite}}
@media (min-width:768px){.pc-semanas td.d{white-space:nowrap}}
@media (min-width:1024px){.pc-grupos.lado{grid-template-columns:repeat(var(--n,1),minmax(0,1fr))}}
@media (max-width:1023px){.pc-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:767px){
  .pc-heroi{padding:16px 18px;border-radius:16px}
  .pc-heroi-cab{flex-direction:column}
  .pc-heroi-tit{font-size:18px}
  .pc-heroi-frase{font-size:14px}
  .pc-kpi{padding:12px 14px}.pc-kpi-valor{font-size:24px}.pc-kpi-valor.longo{font-size:19px}.pc-kpi-de{font-size:15px}
  .pc-barrinhas{height:28px}
  .pc-so-largo{display:none}
  .pc-atualizar-txt{display:none}
  .pc-btn.pc-atualizar{width:44px;height:44px;min-height:44px;padding:0}
  .pc-btn,.pc-chip,.pc-seg button{min-height:44px}
  .pc-semanas th,.pc-semanas td{padding:6px 4px;font-size:12px}
  .pc-semanas td:first-child{min-width:0}
  .pc-esq-heroi{min-height:179px}
  .pc-esq-kpi{height:175px}
  /* dentro do app no celular, a barra do topo (59 px, fixa) cobre o comeco da area que rola */
  .app-main .pc-raiz,.app-main #pc-professores,.app-main #pc-ficha{scroll-margin-top:71px}
}
/* O Inicio decide pela largura DELE, nao da janela: em Configuracoes -> Painel no computador
   ele tem ~690 px numa janela de 1470. Navegador sem "container query" fica so' com as regras
   de cima (pela janela). */
.pc-inicio{container-type:inline-size}
@container (max-width:1023px){
  .pc-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}
  .pc-grupos.lado{grid-template-columns:minmax(0,1fr)}
}
@media (max-width:359px){.pc-kpi-valor{font-size:21px}.pc-kpi-valor.longo{font-size:17px}}
@media (min-width:768px){.pc-so-estreito{display:none}}
@media (max-width:520px){.pc-cartoes{grid-template-columns:repeat(2,minmax(0,1fr))}}
@keyframes pc-entra{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:no-preference){.pc-entra{animation:pc-entra .18s ease-out}}
`;

// ---- Icones (traco, herdam a cor) ------------------------------------------------
const ICONES = {
  alerta: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  sumindo: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="23" y1="11" x2="17" y2="11" /></>,
  nunca: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="18" y1="8" x2="23" y2="13" /><line x1="23" y1="8" x2="18" y2="13" /></>,
  funil: <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,
  erro: <><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
  pausa: <><circle cx="12" cy="12" r="10" /><line x1="10" y1="15" x2="10" y2="9" /><line x1="14" y1="15" x2="14" y2="9" /></>,
  ok: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  atualizar: <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
  fechar: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  direita: <polyline points="9 18 15 12 9 6" />,
  baixo: <polyline points="6 9 12 15 18 9" />,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  banco: <><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></>,
  cadeado: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  busca: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
};
function Icone({ n, c, s = 16 }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-0.15em', flex: '0 0 auto' }}>
      {ICONES[n] || null}
    </svg>
  );
}

// ---- Pecas pequenas ------------------------------------------------------------
function Cartao({ children, style }) {
  return <div style={Object.assign({ background: C.branco, border: '1px solid ' + C.borda, borderRadius: 16, padding: 16, minWidth: 0 }, style || {})}>{children}</div>;
}
function Titulo({ children, sub, direita }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: F_TIT, fontSize: 15, fontWeight: 700, color: C.tinta, letterSpacing: '-0.01em', lineHeight: 1.3 }}>{children}</div>
        {sub ? <div style={{ fontSize: 12.5, color: C.apoio, marginTop: 3, lineHeight: 1.4 }}>{sub}</div> : null}
      </div>
      {direita || null}
    </div>
  );
}
function Selo({ bg, tx, children, title }) {
  return <span title={title} style={{ display: 'inline-flex', alignItems: 'center', background: bg, color: tx, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.5 }}>{children}</span>;
}
function SeloPapel({ papel }) {
  const p = PAPEL[papel] || PAPEL.visitante;
  return <Selo bg={p.bg} tx={p.tx}>{p.r}</Selo>;
}
// Estado da conta (nova, ativa, esfriando...): selo neutro — verde, ambar e vermelho ficam so'
// para a situacao do professor e para erro (principio 12)
function SeloStatus({ status }) {
  const s = STATUS[status];
  return s ? <Selo bg="#F0F1F4" tx={C.apoio} title={s.dica}>{s.r}</Selo> : null;
}
function Numero({ rotulo, valor, sub, ponto }) {
  return (
    <div style={{ background: C.branco, border: '1px solid ' + C.borda, borderRadius: 14, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.3 }}>
        {ponto ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: ponto, flex: '0 0 auto' }} /> : null}
        <span>{rotulo}</span>
      </div>
      <div style={{ fontFamily: F_TIT, fontSize: 24, fontWeight: 800, color: C.tinta, marginTop: 4, lineHeight: 1.15 }}>{valor}</div>
      {sub ? <div style={{ fontSize: 12, color: C.apoio, marginTop: 4, lineHeight: 1.35 }}>{sub}</div> : null}
    </div>
  );
}
function Vazio({ children }) {
  return <div style={{ padding: '18px 12px', textAlign: 'center', color: C.apoio, fontSize: 13, lineHeight: 1.5 }}>{children}</div>;
}
function Aviso({ cor = 'azul', icone = 'info', children }) {
  const p = { azul: [C.azulClaro, '#C2D4FF', C.azulEsc], ambar: [C.ambarClaro, '#FCD9A0', C.ambarEsc], verm: [C.vermClaro, '#FECACA', C.vermEsc] }[cor];
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: p[0], border: '1px solid ' + p[1], borderRadius: 14, padding: '12px 14px', color: p[2], fontSize: 13, lineHeight: 1.5 }}>
      <span style={{ marginTop: 2 }}><Icone n={icone} /></span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}
function Medidor({ v, max, cor = C.azul }) {
  const p = max > 0 ? Math.max(0, Math.min(100, (num(v) / max) * 100)) : 0;
  return (
    <div style={{ height: 6, background: C.linha, borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ width: p + '%', minWidth: num(v) > 0 ? 3 : 0, height: '100%', background: cor, borderRadius: 3 }} />
    </div>
  );
}
function LinhaBarra({ rotulo, sub, direita, v, max, cor }) {
  return (
    <div style={{ padding: '9px 0', borderTop: '1px solid ' + C.linha }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.tinta, lineHeight: 1.35 }}>{rotulo}</div>
          {sub ? <div style={{ fontSize: 12, color: C.apoio, lineHeight: 1.35, marginTop: 1 }}>{sub}</div> : null}
        </div>
        <div className="pc-num" style={{ fontSize: 13.5, fontWeight: 800, color: C.tinta, whiteSpace: 'nowrap' }}>{direita}</div>
      </div>
      <Medidor v={v} max={max} cor={cor} />
    </div>
  );
}
function Segmentado({ opcoes, valor, onChange, rotulo }) {
  return (
    <div className="pc-seg" role="group" aria-label={rotulo}>
      {opcoes.map(([v, l]) => (
        <button key={String(v)} type="button" className={valor === v ? 'on' : ''} aria-pressed={valor === v} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}
function Chips({ opcoes, valor, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {opcoes.map(o => (
        <button key={o.k} type="button" className={'pc-chip' + (valor === o.k ? ' on' : '')} aria-pressed={valor === o.k} onClick={() => onChange(o.k)}>
          {o.cor ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.cor }} /> : null}
          {o.l}{o.n != null ? <span style={{ opacity: 0.7, fontWeight: 600 }}>{fmtN(o.n)}</span> : null}
        </button>
      ))}
    </div>
  );
}
// Lista longa: mostra os primeiros e um "ver todos"
function ListaCurta({ itens, inicial = 8, render, vazio }) {
  const [todos, setTodos] = React.useState(false);
  const l = arr(itens);
  if (!l.length) return vazio ? <Vazio>{vazio}</Vazio> : null;
  const vis = todos ? l : l.slice(0, inicial);
  return (
    <div>
      {vis.map(render)}
      {l.length > inicial ? (
        <button type="button" className="pc-link" onClick={() => setTodos(!todos)}>{todos ? 'Mostrar menos' : 'Ver todos (' + fmtN(l.length) + ')'}</button>
      ) : null}
    </div>
  );
}

// ---- Grafico (SVG proprio) --------------------------------------------------------
// tipo 'colunas' (empilha quando ha' mais de uma serie; `negativa` desce abaixo do zero),
// 'linhas' (null = buraco: semana sem medicao) e 'area' (faixas empilhadas).
// Toque/mouse mostra o valor exato; "Ver números" abre a tabela do grafico.
function useLargura() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const medir = () => { const x = Math.floor(el.getBoundingClientRect().width); if (x > 0) setW(p => (p === x ? p : x)); };
    medir();
    let ro = null;
    try { if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(medir); ro.observe(el); } } catch (e) {}
    window.addEventListener('resize', medir);
    return () => { window.removeEventListener('resize', medir); try { if (ro) ro.disconnect(); } catch (e) {} };
  }, []);
  return [ref, w];
}
function passoBonito(bruto) {
  if (!(bruto > 0)) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(bruto)));
  const f = bruto / exp;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * exp;
}
function escala(min, max) {
  if (max <= 0 && min >= 0) max = 1;
  let passo = Math.max(1, passoBonito((max - min) / 4));
  const lo = Math.floor(min / passo) * passo;
  let hi = Math.ceil(max / passo) * passo;
  if (hi <= lo) hi = lo + passo;
  const ticks = [];
  for (let v = lo; v <= hi + passo / 1000; v += passo) ticks.push(Math.round(v * 1000) / 1000);
  return { lo, hi, ticks };
}
const r1 = v => Math.round(v * 10) / 10;
function barraCima(x, yt, w, yb, r) {
  const h = yb - yt; if (h <= 0) return '';
  const k = Math.max(0, Math.min(r, w / 2, h));
  x = r1(x); yt = r1(yt); yb = r1(yb); w = r1(w);
  if (!k) return 'M' + x + ',' + yb + 'V' + yt + 'H' + r1(x + w) + 'V' + yb + 'Z';
  return 'M' + x + ',' + yb + 'V' + r1(yt + k) + 'Q' + x + ',' + yt + ' ' + r1(x + k) + ',' + yt + 'H' + r1(x + w - k) + 'Q' + r1(x + w) + ',' + yt + ' ' + r1(x + w) + ',' + r1(yt + k) + 'V' + yb + 'Z';
}
function barraBaixo(x, yt, w, yb, r) {
  const h = yb - yt; if (h <= 0) return '';
  const k = Math.max(0, Math.min(r, w / 2, h));
  x = r1(x); yt = r1(yt); yb = r1(yb); w = r1(w);
  if (!k) return 'M' + x + ',' + yt + 'V' + yb + 'H' + r1(x + w) + 'V' + yt + 'Z';
  return 'M' + x + ',' + yt + 'V' + r1(yb - k) + 'Q' + x + ',' + yb + ' ' + r1(x + k) + ',' + yb + 'H' + r1(x + w - k) + 'Q' + r1(x + w) + ',' + yb + ' ' + r1(x + w) + ',' + r1(yb - k) + 'V' + yt + 'Z';
}
const LARG_CHAR = 6.4;   // largura media de um caractere a 11-11,5 px (Manrope)

function Grafico({ titulo, itens, x, rotuloX = 'Semana', fmtX = fmtDiaMes, fmtXLongo, series, tipo = 'colunas', negativa, fmt = fmtN, altura = 200, rotular = 'ultimo', extra, vazio }) {
  const [ref, larg] = useLargura();
  const [idx, setIdx] = React.useState(null);
  const [verTabela, setVerTabela] = React.useState(false);
  const lista = arr(itens);
  const n = lista.length;
  const longo = fmtXLongo || fmtX;
  const val = (it, k) => (it == null || it[k] == null || it[k] === '' ? null : num(it[k]));
  const ehColuna = tipo === 'colunas';
  const empilha = series.length > 1 && tipo !== 'linhas';
  const topoDe = i => series.reduce((a, se) => a + Math.max(0, val(lista[i], se.k) || 0), 0);

  // valores (area empilhada: acumulado de baixo para cima)
  const cols = series.map(se => lista.map(it => val(it, se.k)));
  let tops = cols;
  if (empilha && !ehColuna) {
    const acc = lista.map(() => 0);
    tops = cols.map(c => c.map((v, i) => { acc[i] += Math.max(0, v || 0); return acc[i]; }));
  }
  let maxV = 0, minV = 0, temValor = false;
  lista.forEach((it, i) => {
    if (empilha) { maxV = Math.max(maxV, topoDe(i)); }
    series.forEach((se, k) => { const v = cols[k][i]; if (v != null) { temValor = true; if (!empilha) maxV = Math.max(maxV, v); } });
    if (negativa) { const v = Math.abs(val(it, negativa.k) || 0); if (v) temValor = true; minV = Math.min(minV, -v); }
  });

  const legenda = series.map(se => ({ cor: se.cor, r: se.rotulo })).concat(negativa ? [{ cor: negativa.cor, r: negativa.rotulo }] : []);
  const comLegenda = legenda.length >= 2;

  let svg = null, dica = null;
  if (larg > 0 && n > 0 && temValor) {
    const W = larg, H = altura;
    const esc = escala(minV, maxV);
    const fmtTick = t => (t < 0 ? '−' + fmt(-t) : fmt(t));
    const padL = Math.round(Math.max.apply(null, esc.ticks.map(t => fmtTick(t).length)) * LARG_CHAR) + 10;
    const padR = 8, padT = 18, padB = 24;
    const x0 = padL, x1 = Math.max(padL + 20, W - padR), y0 = padT, y1 = H - padB;
    const py = v => y1 - ((v - esc.lo) / (esc.hi - esc.lo)) * (y1 - y0);
    const slot = (x1 - x0) / n;
    const passoX = n > 1 ? (x1 - x0) / (n - 1) : 0;
    const px = i => (ehColuna ? x0 + slot * (i + 0.5) : n <= 1 ? (x0 + x1) / 2 : x0 + passoX * i);
    const larB = Math.max(1, Math.min(24, slot >= 8 ? slot * 0.68 : slot - 1));
    const ancora = (xp, txt) => {
      const w = String(txt).length * LARG_CHAR;
      if (xp + w / 2 > W - 2) return { x: W - 2, a: 'end' };
      if (xp - w / 2 < x0 - 4) return { x: Math.max(0, xp - 4), a: 'start' };
      return { x: xp, a: 'middle' };
    };

    const grade = esc.ticks.map(t => (
      <g key={'t' + t}>
        <line x1={x0} x2={x1} y1={r1(py(t))} y2={r1(py(t))} stroke={t === 0 ? C.eixo : C.linha} strokeWidth="1" />
        <text x={x0 - 6} y={r1(py(t) + 4)} textAnchor="end" fontSize="11" fill={C.apoio} className="pc-num">{fmtTick(t)}</text>
      </g>
    ));

    const fundos = [], marcas = [], rotulos = [];
    if (idx != null && idx < n) {
      if (ehColuna) fundos.push(<rect key="hl" x={r1(x0 + slot * idx)} y={y0} width={r1(slot)} height={y1 - y0} fill={C.azul} opacity="0.07" />);
      else fundos.push(<line key="hl" x1={r1(px(idx))} x2={r1(px(idx))} y1={y0} y2={y1} stroke={C.eixo} strokeWidth="1" />);
    }

    if (ehColuna) {
      lista.forEach((it, i) => {
        const xl = px(i) - larB / 2;
        let acum = 0;
        const segs = series.map(se => ({ se, y: Math.max(0, val(it, se.k) || 0) })).filter(s => s.y > 0);
        segs.forEach((s, j) => {
          const yb = py(acum) - (j > 0 ? 2 : 0);
          acum += s.y;
          const yt = py(acum);
          if (yb - yt < 0.5) return;
          marcas.push(<path key={'c' + i + '_' + j} d={barraCima(xl, yt, larB, yb, j === segs.length - 1 ? 4 : 0)} fill={s.se.cor} />);
        });
        if (negativa) {
          const v = Math.abs(val(it, negativa.k) || 0);
          if (v > 0) marcas.push(<path key={'n' + i} d={barraBaixo(xl, py(0) + 1, larB, py(-v), 4)} fill={negativa.cor} />);
        }
      });
    } else {
      series.forEach((se, k) => {
        const topo = tops[k];
        if (empilha) {
          const base = k ? tops[k - 1] : lista.map(() => 0);
          const cima = topo.map((v, i) => r1(px(i)) + ',' + r1(py(v)));
          const baixo = base.map((v, i) => r1(px(i)) + ',' + r1(py(v))).reverse();
          fundos.push(<polygon key={'a' + k} points={cima.concat(baixo).join(' ')} fill={se.cor} fillOpacity="0.16" />);
          if (n > 1) marcas.push(<polyline key={'l' + k} points={cima.join(' ')} fill="none" stroke={se.cor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />);
          return;
        }
        const trechos = [];
        let atual = [];
        topo.forEach((v, i) => { if (v == null) { if (atual.length) trechos.push(atual); atual = []; } else atual.push(i); });
        if (atual.length) trechos.push(atual);
        trechos.forEach((t, j) => {
          const pts = t.map(i => r1(px(i)) + ',' + r1(py(topo[i])));
          if (se.area && t.length > 1) {
            fundos.push(<polygon key={'w' + k + '_' + j} points={pts.concat([r1(px(t[t.length - 1])) + ',' + r1(py(0)), r1(px(t[0])) + ',' + r1(py(0))]).join(' ')} fill={se.cor} fillOpacity="0.1" />);
          }
          if (t.length > 1) marcas.push(<polyline key={'l' + k + '_' + j} points={pts.join(' ')} fill="none" stroke={se.cor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />);
          else marcas.push(<circle key={'p' + k + '_' + j} cx={r1(px(t[0]))} cy={r1(py(topo[t[0]]))} r="4" fill={se.cor} stroke="#fff" strokeWidth="2" />);
        });
      });
    }

    // Rotulos diretos, poucos: o ultimo valor (e o maior, quando pedido). O contorno branco
    // e' um texto separado por baixo (paint-order nao existe em todo WebView).
    const rotulo = (key, xp, yp, txt) => {
      const a = ancora(xp, txt);
      const at = { x: r1(a.x), y: r1(yp), textAnchor: a.a, fontSize: '11.5', fontWeight: '700', className: 'pc-num' };
      rotulos.push(<text key={key + 'h'} {...at} fill="#fff" stroke="#fff" strokeWidth="3" strokeLinejoin="round" aria-hidden="true">{txt}</text>);
      rotulos.push(<text key={key} {...at} fill={C.tinta}>{txt}</text>);
    };
    if (rotular && (ehColuna || empilha)) {
      const alvo = [n - 1];
      if (rotular === 'ultimo_max') {
        let im = -1, vm = 0;
        for (let i = 0; i < n; i++) { const t = topoDe(i); if (t > vm) { vm = t; im = i; } }
        if (im >= 0 && im !== n - 1 && Math.abs(px(im) - px(n - 1)) > 70) alvo.push(im);
      }
      alvo.forEach(i => { const t = topoDe(i); if (t > 0) rotulo('r' + i, px(i), py(t) - 6, fmt(t)); });
      if (!ehColuna && n > 1) {
        const i = n - 1;
        marcas.push(<circle key="fimA" cx={r1(px(i))} cy={r1(py(topoDe(i)))} r="4" fill={series[series.length - 1].cor} stroke="#fff" strokeWidth="2" />);
      }
    } else if (rotular && !ehColuna) {
      const fins = [];
      series.forEach((se, k) => {
        let i = n - 1;
        while (i >= 0 && tops[k][i] == null) i--;
        if (i >= 0) fins.push({ k, i, v: tops[k][i], cor: se.cor });
      });
      fins.sort((a, b) => py(a.v) - py(b.v));
      let ultY = -99;
      fins.forEach((f, j) => {
        const yp = py(f.v);
        marcas.push(<circle key={'fim' + f.k} cx={r1(px(f.i))} cy={r1(yp)} r="4" fill={f.cor} stroke="#fff" strokeWidth="2" />);
        let ty = yp - 9;
        if (j > 0 && Math.abs(ty - ultY) < 14) ty = yp + 18;
        ultY = ty;
        rotulo('rf' + f.k, px(f.i), ty, fmt(f.v));
      });
    }

    // Eixo X: rotulos espacados, o ultimo sempre aparece
    const maxRot = Math.max(2, Math.floor((x1 - x0) / 46));
    const pRot = Math.max(1, Math.ceil(n / maxRot));
    const eixoX = [];
    lista.forEach((it, i) => {
      if ((n - 1 - i) % pRot) return;
      const t = fmtX(it[x]);
      const a = ancora(px(i), t);
      eixoX.push(<text key={'x' + i} x={r1(a.x)} y={H - 7} textAnchor={a.a} fontSize="11" fill={C.apoio}>{t}</text>);
    });

    // Destaque do ponto sob o dedo/mouse
    if (idx != null && idx < n && !ehColuna) {
      series.forEach((se, k) => {
        const v = tops[k][idx];
        if (v != null) marcas.push(<circle key={'h' + k} cx={r1(px(idx))} cy={r1(py(v))} r="4.5" fill={se.cor} stroke="#fff" strokeWidth="2" />);
      });
    }

    const mover = e => {
      const r = e.currentTarget.getBoundingClientRect();
      const xx = (e.clientX - r.left) * (W / (r.width || W));
      let i = ehColuna ? Math.floor((xx - x0) / (slot || 1)) : Math.round((xx - x0) / (passoX || 1));
      if (!Number.isFinite(i)) i = n - 1;
      setIdx(Math.max(0, Math.min(n - 1, i)));
    };
    const teclas = e => {
      if (e.key === 'ArrowRight') { e.preventDefault(); setIdx(i => (i == null ? 0 : Math.min(n - 1, i + 1))); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setIdx(i => (i == null ? n - 1 : Math.max(0, i - 1))); }
      else if (e.key === 'Escape') setIdx(null);
    };
    svg = (
      <svg width={W} height={H} viewBox={'0 0 ' + W + ' ' + H} role="img" tabIndex={0}
        aria-label={(titulo || 'Gráfico') + '. Use as setas para ver cada valor.'}
        style={{ display: 'block', touchAction: 'pan-y', fontFamily: F_CORPO, outline: 'none', cursor: 'crosshair' }}
        onPointerMove={mover} onPointerDown={mover}
        onPointerLeave={e => { if (e.pointerType === 'mouse') setIdx(null); }}
        onKeyDown={teclas} onBlur={() => setIdx(null)}>
        {grade}{fundos}{marcas}{rotulos}{eixoX}
      </svg>
    );

    if (idx != null && lista[idx]) {
      const it = lista[idx];
      const linhas = [];
      (empilha ? series.slice().reverse() : series).forEach(se => linhas.push({ cor: se.cor, r: se.rotulo, v: val(it, se.k) }));
      if (negativa) linhas.push({ cor: negativa.cor, r: negativa.rotulo, v: val(it, negativa.k) });
      if (empilha) linhas.push({ r: 'Total', v: topoDe(idx), forte: true });
      if (extra) arr(extra(it)).forEach(p => linhas.push({ r: p[0], txt: p[1] }));
      const larD = 190;
      const xp = px(idx);
      const esq = xp + 14 + larD <= W ? xp + 14 : Math.max(0, xp - 14 - larD);
      dica = (
        <div style={{ position: 'absolute', top: 4, left: esq, width: larD, background: C.tinta, color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 12, lineHeight: 1.4, pointerEvents: 'none', boxShadow: '0 6px 18px rgba(22,24,29,.22)', zIndex: 3 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>{longo(it[x])}</div>
          {linhas.map((l, j) => (
            <div key={j} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderTop: l.forte ? '1px solid rgba(255,255,255,.18)' : 'none', marginTop: l.forte ? 3 : 0, paddingTop: l.forte ? 3 : 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {l.cor ? <span style={{ width: 8, height: 8, borderRadius: 2, background: l.cor, flex: '0 0 auto' }} /> : null}
                <span style={{ opacity: 0.85 }}>{l.r}</span>
              </span>
              <b className="pc-num" style={{ whiteSpace: 'nowrap' }}>{l.txt != null ? l.txt : l.v == null ? '—' : fmt(l.v)}</b>
            </div>
          ))}
        </div>
      );
    }
  }

  return (
    <div>
      {comLegenda ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: 8, fontSize: 12, color: C.texto }}>
          {legenda.map(l => (
            <span key={l.r} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {tipo === 'linhas'
                ? <span style={{ width: 14, height: 3, borderRadius: 2, background: l.cor }} />
                : <span style={{ width: 10, height: 10, borderRadius: 3, background: l.cor }} />}
              {l.r}
            </span>
          ))}
        </div>
      ) : null}
      <div ref={ref} style={{ position: 'relative', width: '100%', minHeight: temValor && n ? altura : 0 }}>
        {svg}
        {dica}
        {(!n || !temValor) ? <Vazio>{vazio || 'Ainda sem dados para este gráfico.'}</Vazio> : null}
      </div>
      {n && temValor ? (
        <button type="button" className="pc-link" onClick={() => setVerTabela(!verTabela)} aria-expanded={verTabela}>
          {verTabela ? 'Esconder números' : 'Ver números'}
        </button>
      ) : null}
      {verTabela && n ? (
        <div className="pc-rolagem" style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid ' + C.linha, borderRadius: 10 }}>
          <table className="pc-tabela">
            <thead>
              <tr>
                <th>{rotuloX}</th>
                {series.map(se => <th key={se.k} className="d">{se.rotulo}</th>)}
                {negativa ? <th className="d">{negativa.rotulo}</th> : null}
              </tr>
            </thead>
            <tbody>
              {lista.slice().reverse().map((it, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap' }}>{longo(it[x])}</td>
                  {series.map(se => { const v = val(it, se.k); return <td key={se.k} className="d pc-num">{v == null ? '—' : fmt(v)}</td>; })}
                  {negativa ? <td className="d pc-num">{fmt(val(it, negativa.k) || 0)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
function CaixaGrafico({ titulo, observe, nota, children }) {
  return (
    <Cartao>
      <Titulo sub={observe}>{titulo}</Titulo>
      {children}
      {nota ? <div style={{ fontSize: 12, color: C.apoio, marginTop: 4, lineHeight: 1.45 }}>{nota}</div> : null}
    </Cartao>
  );
}

// ---- Funil em degraus -----------------------------------------------------------
// Porcentagem so' com base de 20 ou mais: abaixo disso, so' a contagem.
function Degraus({ degraus, base, desde = 1 }) {
  const topo = Math.max(1, base || 0, degraus.length ? degraus[0].n : 0);
  const comPct = topo >= 20;
  const q = maiorQueda(degraus, desde);
  return (
    <div>
      {degraus.map((g, i) => {
        const ant = i ? degraus[i - 1].n : g.n;
        const perdeu = i ? Math.max(0, ant - g.n) : 0;
        const destaque = q && q.i === i;
        const pctIni = (g.n / topo) * 100;
        return (
          <div key={g.chave} style={{ padding: '8px 10px', margin: '0 -10px', borderRadius: 10, background: destaque ? C.ambarClaro : 'transparent' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
              <span style={{ fontSize: 13, fontWeight: g.fim || i === 0 ? 800 : 600, color: C.tinta, minWidth: 0 }}>{g.rotulo}</span>
              <span className="pc-num" style={{ fontSize: 13, fontWeight: 800, color: C.tinta, whiteSpace: 'nowrap' }}>
                {fmtN(g.n)}{comPct ? <span style={{ color: C.apoio, fontWeight: 600 }}> · {fmtPct(pctIni)}</span> : null}
              </span>
            </div>
            <Medidor v={g.n} max={topo} cor={g.fim ? C.verde : i === 0 ? C.cinzaSerie : C.azul} />
            {(perdeu > 0 || g.seg != null || g.pararam > 0) ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: 5, fontSize: 12, color: C.apoio }}>
                {perdeu > 0 ? (
                  <span style={{ color: destaque ? C.ambarEsc : C.apoio, fontWeight: destaque ? 800 : 600 }}>
                    −{fmtN(perdeu)}{comPct ? ' (' + fmtPct((perdeu / Math.max(1, ant)) * 100) + ')' : ''}{destaque ? ' · maior queda' : ''}
                  </span>
                ) : null}
                {g.pararam > 0 ? <span>{plural(g.pararam, 'parou aqui', 'pararam aqui')}</span> : null}
                {g.seg != null && g.seg > 0 ? <span>tempo típico: {fmtSeg(g.seg)}</span> : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ---- Assuntos de Detalhes (um nivel so': Inicio -> Detalhes -> assunto) ---------------
// periodo: o seletor 7/30/90 dias vale; internos: a caixa "Incluir contas de teste" vale.
// lk = rotulo do link em "Mais detalhes", no fim do Inicio.
const SECOES = [
  { k: 'professores', l: 'Professores', lk: 'Professores (tabela completa)', periodo: false, internos: true },
  { k: 'pessoas', l: 'Pessoas', lk: 'Pessoas', periodo: false, internos: true },
  { k: 'crescimento', l: 'Crescimento', lk: 'Crescimento semana a semana', periodo: false, internos: true },
  { k: 'acessos', l: 'Acessos e aparelhos', lk: 'Acessos e aparelhos', periodo: true, internos: true },
  { k: 'telas', l: 'Telas e botões', lk: 'Telas e botões', periodo: true, internos: true },
  { k: 'desistencias', l: 'Onde desistem', lk: 'Onde desistem', periodo: true, internos: true },
  { k: 'erros', l: 'Erros do app', lk: 'Erros do app', periodo: true, internos: true },
  { k: 'promocoes', l: 'Promoções e prêmios', lk: 'Promoções e prêmios', periodo: false, internos: false },
];
const TXT_SEM_EVENTOS = 'Os dados de uso começam a aparecer a partir da versão v129 — hoje ainda não há ações registradas.';

// Linha de lista que abre outra coisa ao tocar
function ItemClicavel({ onClick, children }) {
  if (!onClick) return <div style={{ padding: '10px 0', borderTop: '1px solid ' + C.linha }}>{children}</div>;
  return (
    <div style={{ borderTop: '1px solid ' + C.linha }}>
      <button type="button" className="pc-linha-btn" onClick={onClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
          <span style={{ color: C.dica }}><Icone n="direita" /></span>
        </div>
      </button>
    </div>
  );
}
function Linha2({ esq, dir, sub, subCor }) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0, fontSize: 13.5, fontWeight: 700, color: C.tinta }}>{esq}</div>
        {dir ? <div style={{ fontSize: 12.5, fontWeight: 700, color: C.texto, whiteSpace: 'nowrap' }}>{dir}</div> : null}
      </div>
      {sub ? <div style={{ fontSize: 12.5, color: subCor || C.apoio, marginTop: 2, lineHeight: 1.4 }}>{sub}</div> : null}
    </>
  );
}
// CRESCIMENTO: a base inteira, desde a primeira conta
function BarraStatus({ rotulo, valores }) {
  const total = ORDEM_STATUS.reduce((a, k) => a + num(valores[CHAVE_POR_PAPEL[k]]), 0);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, marginBottom: 6 }}>
        <b style={{ color: C.tinta }}>{rotulo}</b>
        <span className="pc-num" style={{ color: C.apoio }}>{plural(total, 'conta', 'contas')}</span>
      </div>
      <div style={{ display: 'flex', gap: 2, height: 16, borderRadius: 6, overflow: 'hidden', background: C.linha }} role="img"
        aria-label={rotulo + ': ' + ORDEM_STATUS.map(k => STATUS[k].rp + ' ' + num(valores[CHAVE_POR_PAPEL[k]])).join(', ')}>
        {total > 0 ? ORDEM_STATUS.map(k => {
          const v = num(valores[CHAVE_POR_PAPEL[k]]);
          return v > 0 ? <div key={k} title={STATUS[k].rp + ': ' + v} style={{ flex: v + ' 0 0', minWidth: 4, background: STATUS[k].cor }} /> : null;
        }) : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: 6, fontSize: 12, color: C.texto }}>
        {ORDEM_STATUS.map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: STATUS[k].cor }} />
            {STATUS[k].rp} <b className="pc-num">{fmtN(valores[CHAVE_POR_PAPEL[k]])}</b>
          </span>
        ))}
      </div>
    </div>
  );
}
// Grafico com ate' 4 pontos vira uma linha de texto: "21/09: 2 · 28/09: 1 · 12/10 (até agora): 1".
// Com mais de uma serie, uma linha por serie ("Professores — 21/09: 0 · ...").
function TextoPontos({ itens, x, fmtX = fmtDiaMes, series, negativa, fmt = fmtN, ultimoCorre, vazio }) {
  const l = arr(itens);
  const todas = series.concat(negativa ? [negativa] : []);
  const temValor = l.some(it => todas.some(se => it[se.k] != null && num(it[se.k]) !== 0));
  if (!l.length || !temValor) return <Vazio>{vazio || 'Ainda sem dados para este gráfico.'}</Vazio>;
  const ponto = (it, i, se) => fmtX(it[x]) + (ultimoCorre && i === l.length - 1 ? ' (até agora)' : '') + ': ' + (it[se.k] == null ? '—' : fmt(it[se.k]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: C.texto, lineHeight: 1.5, padding: '4px 0' }}>
      {todas.map(se => (
        <div key={se.k} className="pc-num">
          {todas.length > 1 ? <b style={{ color: C.tinta }}>{se.rotulo} — </b> : null}
          {l.map((it, i) => ponto(it, i, se)).join(' · ')}
        </div>
      ))}
    </div>
  );
}
function GraficoOuTexto(props) {
  return arr(props.itens).length <= 4 ? <TextoPontos {...props} /> : <Grafico {...props} />;
}

// CRESCIMENTO › "Semana a semana": uma linha por numero, uma coluna por semana
// (8 no computador, as 4 ultimas no celular, sem rolar de lado).
const LINHAS_SEMANA = [
  ['Aulas registradas', s => fmtN(s.aulas_dadas)],
  ['Recebido pelos professores', s => fmtRq(s.gmv_recebido)],
  ['Acordos novos', s => fmtN(s.acordos_novos)],
  ['Contas novas', s => fmtN(s.contas_novas)],
  ['Fichas de aluno (as que existem hoje)', s => fmtN(s.fichas_total)],
  ['Fichas ligadas a uma conta hoje', s => fmtN(s.fichas_com_conta)],
  ['Pessoas que usaram o app', s => (s.ativos_semana == null ? '—' : fmtN(s.ativos_semana))],
];
function TabelaSemanas({ semanas, geradoEm }) {
  const [copiado, setCopiado] = React.useState('');
  const sr = semanasRef(semanas, geradoEm);
  const cols = arr(semanas).slice(-8);
  const soLargo = i => i < cols.length - 4;
  const cab = s => fmtSemanaCurta(s) + (s === sr.atual ? ' (até agora)' : '');
  const copiar = () => {
    const txt = [['Semana'].concat(cols.map(cab)).join('\t')]
      .concat(LINHAS_SEMANA.map(([r, f]) => [r].concat(cols.map(f)).join('\t'))).join('\n');
    let p = null;
    try { p = navigator.clipboard.writeText(txt); } catch (e) { p = null; }
    Promise.resolve(p).then(() => { if (!p) throw new Error('sem area de transferencia'); setCopiado('Copiado'); },
      () => setCopiado('Não deu para copiar'))
      .catch(() => setCopiado('Não deu para copiar'))
      .then(() => setTimeout(() => setCopiado(''), 2000));
  };
  return (
    <Cartao>
      <Titulo sub="Semanas de segunda a domingo; a última ainda está correndo."
        direita={cols.length ? <button type="button" className="pc-btn peq" onClick={copiar}>{copiado || 'Copiar tabela'}</button> : null}>
        Semana a semana
      </Titulo>
      {cols.length ? (
        <div className="pc-rolagem">
        <table className="pc-tabela pc-semanas">
          <thead>
            <tr>
              <th>Semana</th>
              {cols.map((s, i) => <th key={chaveSemana(s) || i} className={'d' + (soLargo(i) ? ' pc-so-largo' : '')}>{cab(s)}</th>)}
            </tr>
          </thead>
          <tbody>
            {LINHAS_SEMANA.map(([r, f]) => (
              <tr key={r}>
                <td>{r}</td>
                {cols.map((s, i) => <td key={chaveSemana(s) || i} className={'d pc-num' + (soLargo(i) ? ' pc-so-largo' : '')}>{f(s)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : <Vazio>Ainda não há semanas para mostrar.</Vazio>}
      <div style={{ fontSize: 12, color: C.apoio, marginTop: 8, lineHeight: 1.45 }}>
        As fichas contam só as que existem hoje, pela semana em que foram criadas; ficha apagada some também do passado.
      </div>
    </Cartao>
  );
}

function AbaCrescimento({ d }) {
  const ct = d.contas, pp = ct.por_papel, cr = d.crescimento;
  const sem = cr.semanas, meses = cr.meses;
  const ult = sem.length ? sem[sem.length - 1] : {};
  const iniMed = (sem.find(s => s.ativos_semana != null) || {}).semana || null;
  const temOutras = sem.some(s => num(s.contas_total) > num(s.prof_total) + num(s.alunos_total));
  const semanas = temOutras ? sem.map(s => Object.assign({}, s, { outras_total: Math.max(0, num(s.contas_total) - num(s.prof_total) - num(s.alunos_total)) })) : sem;
  const div = k => fmtN(pp.professor[k]) + ' prof. · ' + fmtN(pp.aluno[k]) + ' alunos';
  const ultimoMes = meses.length ? meses[meses.length - 1] : null;
  const extraSemana = s => [['Professores novos', fmtN(s.prof_novos)], ['Alunos novos', fmtN(s.alunos_novos)]];
  const corre = !!semanasRef(sem, d.gerado_em).atual;
  const mau = num(d.resumo.mau);
  const fichas = num(ult.fichas_total), ligadas = num(ult.fichas_com_conta);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 12.5, color: C.apoio }}>Desde a primeira conta.</div>
      <div className="pc-cartoes">
        <Numero rotulo="Contas" valor={fmtN(ct.total)} sub={fmtN(ct.professores) + ' professores · ' + fmtN(ct.alunos) + ' alunos' + (num(ct.sem_papel) ? ' · ' + fmtN(ct.sem_papel) + ' sem papel' : '')} />
        <Numero rotulo="Ativas" ponto={STATUS.ativa.cor} valor={fmtN(ct.ativas)} sub={div('ativas')} />
        <Numero rotulo="Nunca usaram" ponto={STATUS.nunca_usou.cor} valor={fmtN(ct.nunca_usou)} sub={div('nunca_usou')} />
        <Numero rotulo="Excluídas" valor={fmtN(ct.excluidas_total)} sub={fmtN(ct.excluidas_periodo) + ' nos últimos ' + fmtN(d.dias || 30) + ' dias'} />
      </div>

      <div className="pc-grade">
        <Cartao>
          <Titulo sub="Cada conta está em um só estado: nova, ativa, esfriando, inativa ou nunca usou.">Em que pé estão as contas</Titulo>
          <BarraStatus rotulo="Professores" valores={pp.professor} />
          <BarraStatus rotulo="Alunos" valores={pp.aluno} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12.5, color: C.texto, borderTop: '1px solid ' + C.linha, paddingTop: 10 }}>
            <span>Avisos no celular ligados: <b className="pc-num">{fmtN(ct.com_push)}</b></span>
            <span>Usaram o app da loja: <b className="pc-num">{fmtN(ct.no_app_nativo)}</b></span>
          </div>
        </Cartao>
        <Cartao>
          <Titulo sub="Quanto o Plen faz parte do dia a dia de quem usa.">Usam quase todo dia?</Titulo>
          {mau >= 10 ? (
            <div style={{ fontFamily: F_TIT, fontSize: 24, fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
              De cada 10 pessoas que usaram no mês, {fmtN(Math.round(cr.dau_mau_pct / 10))} usam num dia comum.
            </div>
          ) : <Vazio>Aparece quando houver 10 pessoas ou mais usando no mês.</Vazio>}
        </Cartao>
      </div>

      <div className="pc-grade">
        <CaixaGrafico titulo="Quantas contas existem" observe="Total acumulado, semana a semana.">
          <GraficoOuTexto titulo="Quantas contas existem" itens={semanas} x="semana" fmtXLongo={fmtSemana} tipo="area" ultimoCorre={corre}
            series={[{ k: 'prof_total', rotulo: 'Professores', cor: C.azul }, { k: 'alunos_total', rotulo: 'Alunos', cor: C.aluno }].concat(temOutras ? [{ k: 'outras_total', rotulo: 'Sem papel', cor: C.visit }] : [])}
            vazio="Ainda não há contas." />
        </CaixaGrafico>
        <CaixaGrafico titulo="Entradas e saídas por semana" observe="Contas criadas e excluídas em cada semana."
          nota={'Saldo dos últimos 30 dias: ' + sinal(cr.crescimento_liquido_30d) + ' ' + (Math.abs(num(cr.crescimento_liquido_30d)) === 1 ? 'conta' : 'contas') + '.'}>
          <GraficoOuTexto titulo="Entradas e saídas por semana" itens={sem} x="semana" fmtXLongo={fmtSemana} ultimoCorre={corre}
            series={[{ k: 'contas_novas', rotulo: 'Contas novas', cor: C.azul }]} negativa={{ k: 'excluidas', rotulo: 'Excluídas', cor: C.verm }}
            extra={extraSemana} vazio="Ainda não há contas." />
        </CaixaGrafico>
        <CaixaGrafico titulo="Quantas contas usaram o app em cada semana" observe="Conta que entrou e fez qualquer coisa na semana."
          nota={iniMed ? 'Começou a medir na semana de ' + fmtDiaMesAno(iniMed) + ' (versão v129).' : null}>
          <GraficoOuTexto titulo="Contas ativas por semana" itens={sem} x="semana" fmtXLongo={fmtSemana} tipo="linhas" ultimoCorre={corre}
            series={[{ k: 'ativos_semana', rotulo: 'Contas ativas', cor: C.azul, area: true }]} vazio="Começa a medir a partir da versão v129 — ainda não há semanas medidas." />
        </CaixaGrafico>
        <CaixaGrafico titulo="Aulas registradas por semana" observe="Aulas marcadas como dadas ou falta, pela data da aula.">
          <GraficoOuTexto titulo="Aulas registradas por semana" itens={sem} x="semana" fmtXLongo={fmtSemana} ultimoCorre={corre}
            series={[{ k: 'aulas_dadas', rotulo: 'Aulas registradas', cor: C.azul }]} rotular="ultimo_max"
            extra={s => [['Acordos novos', fmtN(s.acordos_novos)]]} vazio="Ainda não há aulas registradas." />
        </CaixaGrafico>
        <CaixaGrafico titulo="Recebido pelos professores por mês" observe="Pagamentos marcados como recebidos no mês, sem o saldo antigo lançado no começo."
          nota={ultimoMes ? 'Em ' + fmtMesLongo(ultimoMes.mes) + ' (até agora): ' + fmtR(ultimoMes.gmv_recebido) + '.' : null}>
          <GraficoOuTexto titulo="Recebido pelos professores por mês" itens={meses} x="mes" rotuloX="Mês" fmtX={fmtMes} fmtXLongo={fmtMesLongo} ultimoCorre
            series={[{ k: 'gmv_recebido', rotulo: 'Recebido', cor: C.verde }]} fmt={fmtR} rotular="ultimo_max" vazio="Ainda não há pagamentos recebidos." />
        </CaixaGrafico>
        <CaixaGrafico titulo="Alunos cadastrados × alunos com conta" observe="Alunos cadastrados pelo professor e quantos já têm conta; a diferença são convites ainda não aceitos."
          nota={fichas ? 'Hoje ' + (fichas >= 20 ? fmtPct((ligadas / fichas) * 100) + ' dos alunos cadastrados já têm conta (' + fmtN(ligadas) + ' de ' + fmtN(fichas) + ').' : fmtN(ligadas) + ' de ' + fmtN(fichas) + ' alunos cadastrados já têm conta.') : null}>
          <GraficoOuTexto titulo="Alunos cadastrados e com conta" itens={sem} x="semana" fmtXLongo={fmtSemana} tipo="linhas" ultimoCorre={corre}
            series={[{ k: 'fichas_total', rotulo: 'Cadastrados pelo professor', cor: C.cinzaSerie }, { k: 'fichas_com_conta', rotulo: 'Já têm conta', cor: C.aluno }]}
            vazio="Ainda não há alunos cadastrados." />
        </CaixaGrafico>
      </div>

      <Cartao>
        <Titulo sub="Os mesmos números, mês a mês; o mês atual ainda está correndo.">Mês a mês</Titulo>
        {meses.length ? (
          <div className="pc-rolagem">
            <table className="pc-tabela" style={{ minWidth: 560 }}>
              <thead>
                <tr><th>Mês</th><th className="d">Contas novas</th><th className="d">Excluídas</th><th className="d">Total</th><th className="d">Ativas no mês</th><th className="d">Aulas registradas</th><th className="d">Recebido</th></tr>
              </thead>
              <tbody>
                {meses.slice().reverse().map(m => (
                  <tr key={m.mes}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 700, color: C.tinta }}>{fmtMesLongo(m.mes)}</td>
                    <td className="d pc-num">{fmtN(m.contas_novas)}</td>
                    <td className="d pc-num">{fmtN(m.excluidas)}</td>
                    <td className="d pc-num">{fmtN(m.contas_total)}</td>
                    <td className="d pc-num">{m.ativos_mes == null ? '—' : fmtN(m.ativos_mes)}</td>
                    <td className="d pc-num">{fmtN(m.aulas_dadas)}</td>
                    <td className="d pc-num" style={{ whiteSpace: 'nowrap' }}>{fmtR(m.gmv_recebido)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Vazio>Ainda não há meses para mostrar.</Vazio>}
      </Cartao>
    </div>
  );
}

// ACESSOS: quanta gente, quando, por onde
function TabelaRetencao({ linhas, iniMed }) {
  const hoje = chaveDiaSP(new Date());
  // % so' com 20 contas ou mais na semana; abaixo disso, "X de Y" (contagem)
  const cel = (semana, n, v, contas) => {
    if (!partesData(semana) || diasEntre(hoje, somaDias(semana, 7 * (n + 1))) > 0) {
      return <td className="d" style={{ color: C.dica, textAlign: 'center' }} title="Essa semana ainda não terminou">—</td>;
    }
    if (v == null) {
      return <td className="d" style={{ color: C.dica, textAlign: 'center' }} title="Não dá para medir: semana antes da v129, quando o registro de uso começou">—</td>;
    }
    const p = Math.max(0, Math.min(100, num(v)));
    const a = 0.06 + (p / 100) * 0.64;
    const base = num(contas);
    const txt = base >= 20 ? fmtPct(p) : fmtN(Math.round((p / 100) * base)) + ' de ' + fmtN(base);
    return <td className="pc-num" style={{ background: 'rgba(47,107,255,' + a.toFixed(2) + ')', color: a > 0.42 ? '#fff' : C.tinta, textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>{txt}</td>;
  };
  const l = linhas.slice().sort((a, b) => String(b.semana).localeCompare(String(a.semana)));
  return (
    <div className="pc-rolagem">
      <table className="pc-tabela" style={{ minWidth: 380 }}>
        <thead>
          <tr><th>Criou a conta na semana de</th><th className="d">Contas</th><th style={{ textAlign: 'center' }}>1 semana depois</th><th style={{ textAlign: 'center' }}>2 semanas</th><th style={{ textAlign: 'center' }}>4 semanas</th></tr>
        </thead>
        <tbody>
          {l.map(r => (
            <tr key={r.semana}>
              <td style={{ whiteSpace: 'nowrap' }}>{fmtDiaMesAno(r.semana)}{iniMed && String(r.semana) < String(iniMed) ? <span title="Antes da medição começar" style={{ color: C.dica }}> *</span> : null}</td>
              <td className="d pc-num">{fmtN(r.contas)}</td>
              {cel(r.semana, 1, r.sem1_pct, r.contas)}{cel(r.semana, 2, r.sem2_pct, r.contas)}{cel(r.semana, 4, r.sem4_pct, r.contas)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function AbaAcessos({ d, semEventos, iniMed, versao }) {
  const r = d.resumo, pp = r.por_papel;
  if (semEventos) return <Cartao><Vazio>{TXT_SEM_EVENTOS}</Vazio></Cartao>;
  const plats = r.por_plataforma.slice().sort((a, b) => num(b.usuarios) - num(a.usuarios));
  // quem tem conta mas ainda não escolheu professor/aluno (ou o admin) entra em "usuarios" e em
  // nenhuma faixa: vira uma faixa própria para o total bater
  const porDia = d.por_dia.map(x => Object.assign({}, x, { sem_papel: Math.max(0, num(x.usuarios) - num(x.professores) - num(x.alunos)) }));
  const temSemPapel = porDia.some(x => x.sem_papel > 0);
  const maxPlat = Math.max(1, ...plats.map(p => num(p.usuarios)));
  const versoes = r.por_versao.slice().sort((a, b) => (num(b.usuarios) > 0) - (num(a.usuarios) > 0) || numVersao(b.versao) - numVersao(a.versao));
  // o mesmo nome do Inicio e de Erros: "no ar" e' a versao da faixa "O app"
  const noAr = versao || versaoMaisRecente(d);
  const diasMed = r.dias_medidos == null ? null : num(r.dias_medidos);
  const papel = (rot, p, cor) => (
    <div style={{ padding: '10px 0', borderTop: '1px solid ' + C.linha }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6, fontSize: 13.5 }}>
        <b>{rot}</b>
        <span className="pc-num" style={{ color: C.texto }}>{fmtN(p.ativos_7d)} na semana · {fmtN(p.ativos_30d)} no mês · de {fmtN(p.contas)}</span>
      </div>
      <Medidor v={p.ativos_30d} max={Math.max(1, num(p.contas))} cor={cor} />
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="pc-cartoes">
        <Numero rotulo="Pessoas por dia (média)" valor={fmtDec(r.dau_medio, 1)} sub={diasMed != null && diasMed > 0 && diasMed < num(d.dias || 30) ? 'nos ' + fmtN(diasMed) + ' dias medidos (desde a v129)' : 'no período de ' + fmtN(d.dias || 30) + ' dias'} />
        <Numero rotulo="Usaram na semana" valor={fmtN(r.wau)} sub="pessoas nos últimos 7 dias" />
        <Numero rotulo="Usaram no mês" valor={fmtN(r.mau)} sub="pessoas nos últimos 30 dias" />
        <Numero rotulo="Vezes que abriram o app" valor={fmtN(r.sessoes)} sub={fmtDec(r.sessoes_por_usuario, 1) + ' por pessoa'} />
        <Numero rotulo="Tempo por vez" valor={fmtMin(r.minutos_medios_sessao)} sub="em média, com a tela aberta" />
        <Numero rotulo="Visitas sem conta" valor={fmtN(r.visitantes_sem_conta)} sub="abriram e não entraram" />
      </div>

      <div className="pc-grade">
        <CaixaGrafico titulo="Quem usou o app em cada dia"
          observe="Pessoas com conta e visitas sem conta (quem abre 3 vezes sem conta conta 3).">
          <Grafico titulo="Pessoas e visitas por dia" itens={porDia} x="dia" rotuloX="Dia" fmtXLongo={fmtDiaLongo}
            series={[{ k: 'professores', rotulo: 'Professores', cor: C.azul }, { k: 'alunos', rotulo: 'Alunos', cor: C.aluno }]
              .concat(temSemPapel ? [{ k: 'sem_papel', rotulo: 'Conta sem escolher professor ou aluno', cor: C.cinzaSerie }] : [])
              .concat([{ k: 'visitantes', rotulo: 'Visitas sem conta', cor: C.visit }])}
            extra={x => [['Pessoas com conta', fmtN(x.usuarios)], ['Vezes que abriram o app', fmtN(x.sessoes)], ['Ações registradas', fmtN(x.eventos)], ['Erros', fmtN(x.erros)]]} />
        </CaixaGrafico>
        <Cartao>
          <Titulo sub="Quantos de cada papel usaram, em relação às contas que existem.">Professores e alunos</Titulo>
          {papel('Professores', pp.professor, C.azul)}
          {papel('Alunos', pp.aluno, C.aluno)}
          <div style={{ marginTop: 14 }}>
            <Titulo sub="Por onde as pessoas abrem o Plen.">Aparelho</Titulo>
            {plats.length ? plats.map(p => (
              <LinhaBarra key={p.plataforma} rotulo={rotPlat(p.plataforma)} v={p.usuarios} max={maxPlat} cor={C.azul}
                direita={plural(p.usuarios, 'pessoa', 'pessoas')} sub={'abriram ' + plural(p.sessoes, 'vez', 'vezes')} />
            )) : <Vazio>Sem dados de aparelho.</Vazio>}
          </div>
        </Cartao>
      </div>

      <div className="pc-grade">
        <Cartao>
          <Titulo sub="Quem ainda está numa versão velha e quantos erros cada versão deu.">Versões em uso</Titulo>
          {versoes.length ? (
            <div className="pc-rolagem">
              <table className="pc-tabela">
                <thead><tr><th>Versão</th><th className="d">Pessoas</th><th className="d">Vezes que abriram</th><th className="d">Erros</th></tr></thead>
                <tbody>
                  {versoes.map((v, i) => (
                    <tr key={v.versao || i}>
                      <td style={{ fontWeight: 700, color: C.tinta }}>{curtaVersao(v.versao)}{noAr && v.versao === noAr ? <span style={{ marginLeft: 6 }}><Selo bg="#E9EBEF" tx={C.tinta}>no ar</Selo></span> : null}{!num(v.usuarios) ? <span style={{ marginLeft: 6 }}><Selo bg="#F0F1F4" tx={C.apoio} title="Só apareceu em quem não estava logado">sem login</Selo></span> : null}</td>
                      <td className="d pc-num">{fmtN(v.usuarios)}</td>
                      <td className="d pc-num">{fmtN(v.sessoes)}</td>
                      <td className="d pc-num" style={{ color: num(v.erros) ? C.vermEsc : C.texto, fontWeight: num(v.erros) ? 800 : 400 }}>{fmtN(v.erros)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Vazio>Sem versões registradas.</Vazio>}
        </Cartao>
        <Cartao>
          <Titulo sub="Das contas criadas em cada semana, quantas voltaram a usar 1, 2 e 4 semanas depois.">Voltaram a usar?</Titulo>
          {d.retencao.length ? <TabelaRetencao linhas={d.retencao} iniMed={iniMed} /> : <Vazio>Ainda sem semanas para comparar.</Vazio>}
          {iniMed && d.retencao.some(x => String(x.semana) < String(iniMed)) ? (
            <div style={{ fontSize: 12, color: C.apoio, marginTop: 8 }}>* Uso medido só a partir de {fmtDiaMesAno(iniMed)}: “—” é semana que não dá para medir; as que pegam só parte da medição aparecem mais baixas do que foram.</div>
          ) : null}
        </Cartao>
      </div>
    </div>
  );
}

// USO: telas, janelas, botoes
const AREAS_TELA = [
  { k: 'todas', l: 'Todas' }, { k: 'prof', l: 'Professor' }, { k: 'aluno', l: 'Aluno' }, { k: 'entrada', l: 'Entrada' },
];
const areaTela = c => { const a = String(c || '').split('.')[0]; return a === 'prof' || a === 'aluno' || a === 'entrada' ? a : 'outra'; };
function AbaUso({ d, semEventos }) {
  const [area, setArea] = React.useState('todas');
  if (semEventos) return <Cartao><Vazio>{TXT_SEM_EVENTOS}</Vazio></Cartao>;
  const filtra = l => (area === 'todas' ? l : l.filter(t => areaTela(t.tela) === area));
  const telas = filtra(d.telas.slice().sort((a, b) => num(b.vezes) - num(a.vezes)));
  const maxTela = Math.max(1, ...telas.map(t => num(t.vezes)));
  const mais = telas.slice(0, 10);
  const menos = telas.length > 10 ? telas.slice(10).filter(t => num(t.vezes) > 0).reverse().slice(0, 6) : [];
  const vistas = {};
  d.telas.forEach(t => { vistas[t.tela] = true; });
  const nunca = Object.keys(TELAS).filter(k => !vistas[k] && !/^painel\./.test(k) && !/erro/.test(k) && (area === 'todas' || areaTela(k) === area));
  // telas da entrada (antes do login): quem passa por ali ainda não tem conta — conta em visitas
  const comPct = num(d.resumo.mau) >= 20;   // % so' com base de 20 pessoas ou mais
  const linhaTela = t => (
    <LinhaBarra key={t.tela} rotulo={rotTela(t.tela)} v={t.vezes} max={maxTela} cor={C.azul}
      direita={fmtN(t.vezes) + '×'}
      sub={(areaTela(t.tela) === 'entrada' || /^painel\.login$/.test(String(t.tela))
              ? plural(t.sessoes, 'visita', 'visitas') + (num(t.usuarios) ? ' · ' + plural(t.usuarios, 'pessoa', 'pessoas') + ' com conta' : '')
              : (comPct ? fmtPct(t.pct_ativos) + ' dos ativos · ' : '') + plural(t.usuarios, 'pessoa', 'pessoas'))
           + (num(t.segundos_medios) ? ' · ' + fmtSeg(t.segundos_medios) + ' em média' : '')} />
  );
  const janelas = d.janelas.slice().sort((a, b) => num(b.vezes) - num(a.vezes));
  const maxJan = Math.max(1, ...janelas.map(j => num(j.vezes)));
  const acoes = d.acoes.slice().sort((a, b) => num(b.vezes) - num(a.vezes));
  const maxAc = Math.max(1, ...acoes.map(a => num(a.vezes)));
  const usadas = {};
  acoes.forEach(a => { usadas[String(a.acao || '').replace(/^acao\./, '')] = true; });
  const acoesNunca = Object.keys(ACOES).filter(k => !usadas[k]);
  const serv = d.acoes_servidor.slice().sort((a, b) => num(b.vezes) - num(a.vezes));
  const maxServ = Math.max(1, ...serv.map(a => num(a.vezes)));
  const chipsNunca = (lista, rot) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {lista.map(k => <span key={k} title={k} style={{ border: '1px dashed ' + C.eixo, borderRadius: 999, padding: '4px 10px', fontSize: 12, color: C.texto, background: C.fundo }}>{rot(k)}</span>)}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Chips opcoes={AREAS_TELA} valor={area} onChange={setArea} />
      <div className="pc-grade">
        <Cartao>
          <Titulo sub="Quantas vezes cada tela foi aberta no período e por quantas pessoas.">Telas mais usadas</Titulo>
          {mais.length ? mais.map(linhaTela) : <Vazio>Nenhuma tela aberta nesta parte do app.</Vazio>}
        </Cartao>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <Cartao>
            <Titulo sub="Abertas, mas pouco: pode ser que ninguém ache o caminho até elas.">Telas menos usadas</Titulo>
            {menos.length ? menos.map(linhaTela) : <Vazio>Poucas telas no período para separar as menos usadas.</Vazio>}
          </Cartao>
          <Cartao>
            <Titulo sub="Telas que existem no app e ninguém abriu no período.">Nunca abertas no período</Titulo>
            {nunca.length ? chipsNunca(nunca, rotTela) : <Vazio>Todas as telas conhecidas foram abertas.</Vazio>}
          </Cartao>
        </div>
      </div>
      <div className="pc-grade">
        <Cartao>
          <Titulo sub="Janelas que se abrem por cima da tela (ficha do aluno, convite, recibo...).">Janelas</Titulo>
          <ListaCurta itens={janelas} inicial={8} vazio="Nenhuma janela aberta no período." render={j => (
            <LinhaBarra key={j.nome} rotulo={rotJanela(j.nome)} v={j.vezes} max={maxJan} cor={C.azul} direita={fmtN(j.vezes) + '×'} sub={plural(j.usuarios, 'pessoa', 'pessoas')} />
          )} />
        </Cartao>
        <Cartao>
          <Titulo sub="Botões importantes tocados no app (WhatsApp, Pix, atalhos, filtros...).">Botões usados</Titulo>
          <ListaCurta itens={acoes} inicial={8} vazio="Nenhum botão registrado no período." render={a => (
            <LinhaBarra key={a.acao} rotulo={rotAcao(a.acao)} v={a.vezes} max={maxAc} cor={C.azul} direita={fmtN(a.vezes) + '×'} sub={plural(a.usuarios, 'pessoa', 'pessoas')} />
          )} />
          {acoesNunca.length ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Ninguém tocou no período</div>
              {chipsNunca(acoesNunca, rotAcao)}
            </div>
          ) : null}
        </Cartao>
        <Cartao>
          <Titulo sub="Aulas, pagamentos e acordos que de fato mudaram no banco a partir do app.">O que foi gravado no banco</Titulo>
          <ListaCurta itens={serv} inicial={8} vazio="Nada registrado no período (ou o livro de registros ainda não existe)." render={a => (
            <LinhaBarra key={a.acao} rotulo={rotServidor(a.acao)} v={a.vezes} max={maxServ} cor={C.cinzaSerie} direita={fmtN(a.vezes) + '×'} sub={plural(a.contas, 'conta', 'contas')} />
          )} />
        </Cartao>
      </div>
    </div>
  );
}

// FUNIS: onde as pessoas param
function CartaoFunil({ titulo, sub, degraus, base, desde, rodape }) {
  // primeira etapa com menos de 10 e alguma queda: avisa que e' pouco para concluir
  const ini = degraus.length ? num(degraus[0].n) : 0;
  const caiu = degraus.some((g, i) => i > 0 && num(g.n) < num(degraus[i - 1].n));
  return (
    <Cartao>
      <Titulo sub={sub}>{titulo}</Titulo>
      <Degraus degraus={degraus} base={base} desde={desde} />
      {rodape || null}
      {ini < 10 && caiu ? <div style={{ fontSize: 12, color: C.apoio, marginTop: 8 }}>Poucas tentativas para tirar conclusão ({fmtN(ini)})</div> : null}
    </Cartao>
  );
}
function AbaFunis({ d, semEventos }) {
  if (semEventos) return <Cartao><Vazio>{TXT_SEM_EVENTOS}</Vazio></Cartao>;
  const funis = d.funis.slice().sort((a, b) => num(b.iniciados) - num(a.iniciados));
  const comUso = funis.filter(f => num(f.iniciados) > 0);
  const travas = travasSoltas(d);
  const maxTrava = Math.max(1, ...travas.map(t => t.n));
  const quedas = quedasDosFunis(d);
  const vistos = {};
  funis.forEach(f => { if (num(f.iniciados) > 0) vistos[f.fluxo] = true; });
  const semUso = Object.keys(FLUXOS).filter(k => !vistos[k]);
  const cad = d.cadastro;
  const temCad = PASSOS_CADASTRO.some(([k]) => num(cad[k]) > 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Cartao>
        <Titulo sub="A maior queda de cada passo a passo, só onde houve 10 tentativas ou mais.">Onde mais gente desiste</Titulo>
        <ListaCurta itens={quedas} inicial={4} vazio="Nenhuma queda com 10 tentativas ou mais no período." render={q => (
          <ItemClicavel key={q.chave}>
            <Linha2 esq={q.rotulo} dir={'−' + fmtN(q.perdeu) + (num(q.de.n) >= 20 ? ' (' + fmtPct(q.pct) + ')' : '')}
              sub={<>De “{q.de.rotulo}” para “{q.para.rotulo}” · {plural(q.de.n, 'tentativa chegou', 'tentativas chegaram')} no passo anterior
                {q.bloqueio ? <><br />Motivo mais comum de o app recusar: {rotMotivo(q.bloqueio.motivo)} ({fmtN(q.bloqueio.n)}×)</> : null}</>} />
          </ItemClicavel>
        )} />
      </Cartao>
      <div style={{ fontSize: 12.5, color: C.apoio, lineHeight: 1.5 }}>
        Cada degrau mostra quantas tentativas chegaram até ali. Com 10 tentativas ou mais, a maior queda fica em amarelo.
      </div>
      <div className="pc-grade">
        {temCad ? (
          <CartaoFunil titulo="Entrada no app (por visita)"
            sub="Cada visita à tela de entrada até chegar dentro do app (quem só faz login sai no 1º degrau)."
            degraus={degrausCadastro(cad)} desde={2} />
        ) : null}
        {comUso.map(f => {
          const pct = num(f.iniciados) ? (num(f.concluidos) / num(f.iniciados)) * 100 : 0;
          const bloq = arr(f.bloqueios).slice().sort((a, b) => num(b.n) - num(a.n));
          const res = arr(f.resultados).slice().sort((a, b) => num(b.n) - num(a.n));
          const soConcluiu = res.length === 1 && res[0].resultado === 'concluiu';
          return (
            <CartaoFunil key={f.fluxo} titulo={rotFluxo(f.fluxo)}
              sub={plural(f.iniciados, 'começou', 'começaram') + ' · ' + plural(f.concluidos, 'concluiu', 'concluíram') + (num(f.iniciados) >= 20 ? ' (' + fmtPct(pct) + ')' : '') + (num(f.erros) ? ' · ' + plural(f.erros, 'erro', 'erros') + ' durante' : '')}
              degraus={degrausFluxo(f)} base={num(f.iniciados)}
              rodape={(bloq.length || (res.length && !soConcluiu)) ? (
                <div style={{ marginTop: 10, borderTop: '1px solid ' + C.linha, paddingTop: 8 }}>
                  {res.length && !soConcluiu ? (
                    <div style={{ marginBottom: bloq.length ? 8 : 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>Como terminou</div>
                      <div style={{ fontSize: 12.5, color: C.texto, lineHeight: 1.5 }}>
                        {res.map(x => rotMotivo(x.resultado) + ' ' + fmtN(x.n)).join(' · ')}
                        <span style={{ color: C.apoio }}> — só os de resultado bom contam como “concluíram”.</span>
                      </div>
                    </div>
                  ) : null}
                  {bloq.length ? (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>O app recusou</div>
                      {bloq.slice(0, 6).map((b, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
                          <span style={{ color: C.texto, minWidth: 0 }}>{rotMotivo(b.motivo)} <span style={{ color: C.apoio }}>· em “{rotPasso(f.fluxo, b.passo)}”</span></span>
                          <b className="pc-num">{fmtN(b.n)}×</b>
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null} />
          );
        })}
      </div>
      {!temCad && !comUso.length ? <Cartao><Vazio>Nenhum passo a passo foi usado no período.</Vazio></Cartao> : null}
      {travas.length ? (
        <Cartao>
          <Titulo sub="Senha errada, campo vazio, data inválida... fora de um passo a passo.">O app recusou (fora dos passo a passo)</Titulo>
          <ListaCurta itens={travas} inicial={10} render={(t, i) => (
            <LinhaBarra key={t.fluxo + t.motivo + i} rotulo={rotFluxo(t.fluxo)} sub={'Motivo: ' + rotMotivo(t.motivo)} v={t.n} max={maxTrava} cor={C.ambar} direita={fmtN(t.n) + '×'} />
          )} />
        </Cartao>
      ) : null}
      {semUso.length ? (
        <Cartao>
          <Titulo sub="Passo a passo que ninguém começou no período.">Sem uso no período</Titulo>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {semUso.map(k => <span key={k} style={{ border: '1px dashed ' + C.eixo, borderRadius: 999, padding: '4px 10px', fontSize: 12, color: C.texto, background: C.fundo }}>{rotFluxo(k)}</span>)}
          </div>
        </Cartao>
      ) : null}
    </div>
  );
}

// PESSOAS: ranking, filtros e a linha do tempo de cada um
const COR_TIPO = { tela: C.azul, fluxo: C.azulEsc, ok: C.verde, alerta: C.ambar, erro: C.verm, acao: C.aluno, sessao: C.tinta, mudo: C.eixo };
function descrever(e) {
  const ev = String(e.evento || '');
  const det = e.detalhes && typeof e.detalhes === 'object' ? e.detalhes : {};
  const tela = e.tela ? rotTela(e.tela) : 'uma tela';
  const fl = e.fluxo ? rotFluxo(e.fluxo) : 'um passo a passo';
  const ps = e.fluxo && e.passo != null ? rotPasso(e.fluxo, e.passo) : null;
  if (ev === 'app.abrir') return { t: 'Abriu o app', tipo: 'sessao' };
  if (ev === 'app.remontou') return { t: 'O app se recarregou sozinho', tipo: 'mudo' };
  if (ev === 'tela.ver') return { t: 'Abriu ' + tela, tipo: 'tela' };
  if (ev === 'tela.tempo') return { t: (e.duracao_ms != null ? 'Ficou ' + fmtSeg(num(e.duracao_ms) / 1000) + ' em ' : 'Saiu de ') + tela, tipo: 'mudo' };
  if (ev === 'janela.abrir') return { t: 'Abriu a janela ' + rotJanela(det.nome || 'outra'), tipo: 'tela' };
  if (ev === 'fluxo.inicio') return { t: 'Começou: ' + fl + (ps ? ' (' + ps + ')' : ''), tipo: 'fluxo' };
  if (ev === 'fluxo.passo') return { t: fl + ' → ' + (ps || 'próximo passo'), tipo: 'fluxo' };
  if (ev === 'fluxo.fim') return { t: 'Concluiu: ' + fl + (det.resultado && det.resultado !== 'concluiu' ? ' (' + rotMotivo(det.resultado) + ')' : ''), tipo: 'ok' };
  if (ev === 'fluxo.saiu') return { t: 'Saiu de ' + fl + (ps ? ' em “' + ps + '”' : '') + (det.remontou ? ' (o app recarregou)' : det.motivo ? ' — ' + rotMotivo(det.motivo) : ''), tipo: 'alerta' };
  if (ev === 'aviso.bloqueio') return { t: 'Barrado' + (e.fluxo ? ' em ' + fl : '') + ': ' + rotMotivo(det.motivo), tipo: 'alerta' };
  if (ev === 'aviso.toast') return { t: 'Viu um aviso em ' + tela, tipo: 'mudo' };
  if (ev.indexOf('acao.') === 0) return { t: rotAcao(ev), tipo: 'acao' };
  if (ev.indexOf('erro.') === 0) return { t: rotErro(ev) + (e.erro ? ': ' + e.erro : ''), tipo: 'erro' };
  if (ev === 'sessao.login') return { t: 'Entrou na conta', tipo: 'sessao' };
  if (ev === 'sessao.cadastro') return { t: 'Criou a conta', tipo: 'ok' };
  if (ev === 'sessao.logout') return { t: 'Saiu da conta', tipo: 'sessao' };
  if (ev === 'sessao.conta_excluida') return { t: 'Excluiu a conta', tipo: 'erro' };
  if (ev.indexOf('push.') === 0) return { t: 'Avisos no celular: ' + bonito(ev).toLowerCase(), tipo: 'acao' };
  return { t: bonito(ev), tipo: 'mudo' };
}
function LinhaDoTempo({ eventos }) {
  const [quantos, setQuantos] = React.useState(80);
  const vis = eventos.slice(0, quantos);
  const grupos = [];
  let atual = null;
  vis.forEach(e => {
    const s = e.sessao || '—';
    if (!atual || atual.sessao !== s) { atual = { sessao: s, itens: [] }; grupos.push(atual); }
    atual.itens.push(e);
  });
  if (!eventos.length) return <Vazio>Nenhuma ação registrada para esta pessoa (o registro começou na v129).</Vazio>;
  return (
    <div>
      {grupos.map((g, gi) => {
        const prim = g.itens[g.itens.length - 1], ult = g.itens[0];
        return (
          <div key={gi} style={{ marginTop: gi ? 12 : 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', padding: '6px 0', borderBottom: '1px solid ' + C.linha }}>
              {fmtData(prim.em)} · {fmtHora(prim.em)}–{fmtHora(ult.em)}{prim.plataforma ? ' · ' + (PLAT_CURTA[prim.plataforma] || prim.plataforma) : ''}{prim.versao ? ' · ' + curtaVersao(prim.versao) : ''}
            </div>
            {g.itens.map((e, i) => {
              const ds = descrever(e);
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '5px 0', fontSize: 13, color: ds.tipo === 'mudo' ? C.apoio : C.texto }}>
                  <span className="pc-num" style={{ width: 40, flex: '0 0 auto', color: C.apoio, fontSize: 12 }}>{fmtHora(e.em)}</span>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: COR_TIPO[ds.tipo] || C.eixo, marginTop: 6, flex: '0 0 auto' }} />
                  <span style={{ minWidth: 0, wordBreak: 'break-word', lineHeight: 1.45 }}>{ds.t}</span>
                </div>
              );
            })}
          </div>
        );
      })}
      {eventos.length > quantos ? <button type="button" className="pc-link" onClick={() => setQuantos(quantos + 120)}>Mostrar mais antigos</button> : null}
    </div>
  );
}
// Bloco "No Plen" da ficha de um professor: situacao, caminho, numeros e uso
function BlocoNoPlen({ prof }) {
  const l = prof;
  const parcial = l.etapa === null;
  const passos = [
    'Criou a conta (' + fmtData(l.criado_em) + ')',
    'Cadastrou alunos (' + nOuTraco(l.alunos) + ')',
    'Convidou alunos (' + nOuTraco(l.convites) + ' esperando)',
    'Aluno entrou no app (' + nOuTraco(l.noApp) + ')',
    'Registrou aula (' + nOuTraco(l.aulas30) + ' em 30 dias)',
    'Registrou pagamento',
  ];
  const cab = t => <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', margin: '10px 0 4px' }}>{t}</div>;
  const mes = MESES_LONGOS[(partesData(chaveDiaSP(new Date())) || { m: 1 }).m - 1];
  return (
    <div style={{ background: C.branco, border: '1px solid ' + C.borda, borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', paddingBottom: 6 }}>No Plen</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <SeloSituacao k={l.sit.k} />
        {l.sit.motivo ? <span style={{ fontSize: 12.5, color: SITUACAO[l.sit.k].tx }}>{l.sit.motivo}</span> : null}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: l.sit.cinza ? C.apoio : C.tinta, marginTop: 6 }}>Próximo passo: {l.sit.passo}</div>
      <BotoesContato l={l} />
      {parcial ? (
        <div style={{ fontSize: 12.5, color: C.apoio, marginTop: 10 }}>Não deu para ler alunos, aulas e etapa deste professor agora.</div>
      ) : (
        <>
          {cab('Caminho no Plen')}
          {passos.map((t, k) => (
            <div key={k} style={{ display: 'flex', gap: 8, fontSize: 13, color: l.etapa >= k ? C.tinta : C.apoio, padding: '2px 0' }}>
              <span aria-hidden="true" style={{ width: 14, flex: '0 0 auto', color: l.etapa >= k ? C.tinta : C.eixo, fontWeight: 800 }}>{l.etapa >= k ? '✓' : '○'}</span>
              <span>{t}</span>
            </div>
          ))}
          {cab('Números dele')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: C.texto }}>
            <span>Acordos ativos: <b className="pc-num">{fmtN(l.acordos)}</b></span>
            <span>Aulas: <b className="pc-num">{fmtN(l.aulas7)}</b> em 7 dias · <b className="pc-num">{fmtN(l.aulas30)}</b> em 30 dias</span>
            {/* a ressalva vai escrita (no celular nao ha' "passar o mouse"); o title fica como extra */}
            <span title="Inclui parcelas que ainda vão vencer">Em aberto: <b className="pc-num">{fmtR(l.pendente)}</b> <span style={{ color: C.apoio }}>(inclui parcelas que ainda vão vencer)</span></span>
            {l.fase2 ? <span>Recebido em {mes}: <b className="pc-num">{fmtR(l.recebidoMes)}</b></span> : null}
            <span title={l.fase2 ? undefined : 'Pode incluir saldo antigo lançado no começo'}>
              Recebido desde o início: <b className="pc-num">{fmtR(l.recebidoTotal)}</b>
              {l.fase2 ? null : <span style={{ color: C.apoio }}> (pode incluir saldo antigo lançado no começo)</span>}
            </span>
          </div>
        </>
      )}
      {cab('Uso')}
      <div style={{ fontSize: 13, color: C.texto }}>
        {'Usou em ' + fmtN(l.dias30) + ' dos últimos 30 dias'}
        {l.plataforma ? ' · ' + (PLAT_CURTA[l.plataforma] || l.plataforma) + (l.versao ? ' ' + curtaVersao(l.versao) : '') : ''}
      </div>
    </div>
  );
}
function DetalhePessoa({ supabase, pessoa, prof, onFechar, onMudou }) {
  const [estado, setEstado] = React.useState({ carregando: true, erro: null, cab: {}, eventos: [] });
  const [marcando, setMarcando] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const uid = pessoa.user_id;
  React.useEffect(() => {
    let vivo = true;
    setEstado(s => Object.assign({}, s, { carregando: true, erro: null }));
    Promise.resolve().then(() => supabase.rpc('central_uso_pessoa', { p_user: uid, p_limite: 300 }))
      .then(r => {
        if (!vivo) return;
        if (r && r.error) setEstado({ carregando: false, erro: classificarErro(r.error), cab: {}, eventos: [] });
        else { const n = normalizarPessoa(r && r.data); setEstado({ carregando: false, erro: null, cab: n.cab, eventos: n.eventos }); }
      }, e => { if (vivo) setEstado({ carregando: false, erro: classificarErro(e), cab: {}, eventos: [] }); });
    return () => { vivo = false; };
  }, [uid]);
  const marcar = () => {
    const interna = !pessoa.interno;
    setMarcando(true); setMsg('');
    Promise.resolve().then(() => supabase.rpc('marcar_conta_interna', { p_user: uid, p_interna: interna }))
      .then(r => {
        setMarcando(false);
        if (r && r.error) { setMsg('Não deu para marcar: ' + (r.error.message || 'erro')); return; }
        onMudou(interna ? 'Marcada como conta de teste — sai dos números.' : 'Voltou a contar nos números.');
      }, e => { setMarcando(false); setMsg('Não deu para marcar: ' + String((e && e.message) || e)); });
  };
  const cab = estado.cab || {};
  const email = cab.email || pessoa.email;
  return (
    <div className="pc-entra" style={{ background: C.fundo, border: '1px solid ' + C.borda, borderRadius: 14, padding: 14, margin: '4px 0 10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: F_TIT, fontSize: 15, fontWeight: 700, overflowWrap: 'anywhere' }}>{cab.nome || pessoa.nome || 'Sem nome'}</div>
          <div style={{ fontSize: 12.5, color: C.apoio, wordBreak: 'break-all' }}>{email || ''}</div>
        </div>
        <button type="button" className="pc-btn" onClick={onFechar} aria-label="Fechar" style={{ padding: '6px 10px', minWidth: 44, minHeight: 44 }}><Icone n="fechar" /></button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12.5, color: C.texto, margin: '10px 0' }}>
        <span>Conta criada: <b>{fmtData(cab.criado_em || pessoa.conta_criada_em)}</b></span>
        <span>Último login: <b>{cab.ultimo_login ? fmtDataHora(cab.ultimo_login) : '—'}</b></span>
        <span>Última atividade: <b>{pessoa.status === 'nunca_usou' ? 'só no cadastro' : quando(pessoa.ultima_atividade || pessoa.ultima_vez)}</b></span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button type="button" className="pc-btn" onClick={marcar} disabled={marcando}>
          {marcando ? 'Salvando…' : pessoa.interno ? 'Desmarcar conta de teste' : 'Marcar como conta de teste'}
        </button>
        <span style={{ fontSize: 12, color: C.apoio }}>Conta de teste fica fora dos números.</span>
      </div>
      {msg ? <div style={{ fontSize: 12.5, color: C.vermEsc, marginBottom: 8 }}>{msg}</div> : null}
      {prof ? <BlocoNoPlen prof={prof} /> : null}
      <div style={{ background: C.branco, border: '1px solid ' + C.borda, borderRadius: 12, padding: '8px 12px' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.apoio, textTransform: 'uppercase', letterSpacing: '.04em', padding: '4px 0' }}>Linha do tempo (mais recente primeiro)</div>
        {estado.carregando ? <Vazio>Carregando…</Vazio>
          : estado.erro ? <Vazio>{estado.erro.tipo === 'ausente' ? 'A função central_uso_pessoa ainda não está no banco.' : 'Não deu para carregar: ' + estado.erro.msg}</Vazio>
            : <LinhaDoTempo eventos={estado.eventos} />}
      </div>
    </div>
  );
}
function LinhaPessoa({ p, aberta, onClick, prof }) {
  const ab = p.ultimo_abandono;
  const usou = num(p.dias_ativos_30d) > 0;
  const partes = [
    usou ? 'usou em ' + fmtN(p.dias_ativos_30d) + ' dos últimos 30 dias' : 'sem uso registrado nos últimos 30 dias',
    usou ? 'abriu o app ' + plural(p.sessoes_30d, 'vez', 'vezes') : null,
    usou && num(p.minutos_30d) ? fmtMin(p.minutos_30d) : null,
    p.tela_preferida ? 'mais usa: ' + rotTela(p.tela_preferida) : null,
    p.plataforma ? (PLAT_CURTA[p.plataforma] || p.plataforma) + (p.versao ? ' ' + curtaVersao(p.versao) : '') : null,
    p.conta_criada_em ? 'conta de ' + fmtData(p.conta_criada_em) : null,
  ].filter(Boolean);
  return (
    <div style={{ borderTop: '1px solid ' + C.linha }}>
      <button type="button" className="pc-linha-btn" onClick={onClick} aria-expanded={aberta}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.tinta, overflowWrap: 'anywhere', minWidth: 0 }}>{p.nome || 'Sem nome'}</span>
                {prof && prof.etapa !== null ? <PontosEtapa etapa={prof.etapa} peq /> : null}
                <SeloPapel papel={p.papel} />
                {/* professor do piloto: a mesma palavra e a mesma cor do Inicio ("Parado", nao "Esfriando") */}
                {prof ? <SeloSituacao k={prof.sit.k} /> : <SeloStatus status={p.status} />}
                {p.interno ? <Selo bg="#E9EBEF" tx={C.tinta}>Teste</Selo> : null}
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.texto, whiteSpace: 'nowrap' }}>{p.status === 'nunca_usou' ? 'só no cadastro' : quando(p.ultima_atividade || p.ultima_vez)}</span>
            </div>
            <div style={{ fontSize: 12.5, color: C.apoio, marginTop: 3, lineHeight: 1.45 }}>{partes.join(' · ')}</div>
            {ab && ab.fluxo ? (
              <div style={{ fontSize: 12.5, color: C.ambarEsc, marginTop: 2, lineHeight: 1.45 }}>
                Parou em {rotFluxo(ab.fluxo)}{ab.passo != null ? ' → ' + rotPasso(ab.fluxo, ab.passo) : ''} ({quando(ab.em)})
              </div>
            ) : null}
          </div>
          <span style={{ color: C.dica, marginTop: 2 }}><Icone n={aberta ? 'baixo' : 'direita'} /></span>
        </div>
      </button>
    </div>
  );
}
const estiloSelect = { border: '1.5px solid ' + C.borda, borderRadius: 10, padding: '7px 10px', fontSize: 13, fontWeight: 700, color: C.texto, background: '#fff', minHeight: 44, maxWidth: '100%' };
const ORDENS = [
  { k: 'ativos', l: 'Mais ativos' },
  { k: 'recente', l: 'Atividade mais recente' },
  { k: 'novas', l: 'Contas mais novas' },
  { k: 'nome', l: 'Nome' },
];
function AbaPessoas({ d, supabase, alvo, onMudou, aviso, carteira, onAlvoUsado }) {
  const [status, setStatus] = React.useState('todas');
  const [papel, setPapel] = React.useState('todos');
  const [busca, setBusca] = React.useState('');
  const [ordem, setOrdem] = React.useState('ativos');
  const [aberta, setAberta] = React.useState(null);
  const [noTopo, setNoTopo] = React.useState(false);   // veio de outra aba: a ficha abre no topo
  const [quantos, setQuantos] = React.useState(40);

  React.useEffect(() => {
    if (!alvo) return;
    if (alvo.status) { setStatus(alvo.status); setPapel('todos'); setBusca(''); setAberta(null); }
    if (alvo.papel) { setPapel(alvo.papel); setStatus('todas'); setBusca(''); setAberta(null); }
    if (alvo.user_id) { setStatus('todas'); setPapel('todos'); setBusca(''); setAberta(alvo.user_id); setNoTopo(true); }
    // usado uma vez: se a lista montar de novo (recarga), nao reabre a ficha nem desfaz os filtros
    if (typeof onAlvoUsado === 'function') onAlvoUsado();
  }, [alvo]);
  // ficha aberta a partir de outro lugar: chips, filtros e busca empurram a ficha para baixo da
  // dobra no celular — rola ate' ela (a margem de cima respeita a barra fixa do app)
  React.useEffect(() => {
    if (!(noTopo && aberta)) return;
    try { const el = document.getElementById('pc-ficha'); if (el) el.scrollIntoView({ block: 'start' }); } catch (e) {}
  }, [noTopo, aberta]);
  // marcou/desmarcou teste: a lista recarrega e a pessoa pode sair dela — fecha a ficha
  const mudou = txt => { setAberta(null); onMudou(txt); };

  const contStatus = {};
  const contPapel = { professor: 0, aluno: 0, outros: 0 };
  d.pessoas.forEach(p => {
    contStatus[p.status] = (contStatus[p.status] || 0) + 1;
    if (p.papel === 'professor' || p.papel === 'aluno') contPapel[p.papel]++; else contPapel.outros++;
  });
  const q = semAcento(busca.trim());
  let lista = d.pessoas.filter(p =>
    (status === 'todas' || p.status === status) &&
    (papel === 'todos' || (papel === 'outros' ? p.papel !== 'professor' && p.papel !== 'aluno' : p.papel === papel)) &&
    (!q || semAcento(p.nome).indexOf(q) >= 0 || semAcento(p.email).indexOf(q) >= 0));
  if (ordem !== 'ativos') {
    lista = lista.slice().sort((a, b) => {
      if (ordem === 'nome') return String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
      if (ordem === 'novas') return String(b.conta_criada_em || '').localeCompare(String(a.conta_criada_em || ''));
      return String(b.ultima_atividade || b.ultima_vez || '').localeCompare(String(a.ultima_atividade || a.ultima_vez || ''));
    });
  }
  const vis = lista.slice(0, quantos);
  const abertaVisivel = aberta && vis.some(p => p.user_id === aberta);
  const pessoaAberta = aberta ? (d.pessoas.find(p => p.user_id === aberta) || d.sumindo.find(p => p.user_id === aberta) || { user_id: aberta }) : null;
  const profDe = uid => (carteira && uid ? carteira[uid] || null : null);
  const detalhe = p => <DetalhePessoa key={'d' + p.user_id} supabase={supabase} pessoa={p} prof={profDe(p.user_id)} onFechar={() => setAberta(null)} onMudou={mudou} />;
  const clicar = uid => { setNoTopo(false); setAberta(aberta === uid && !noTopo ? null : uid); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {aviso ? <Aviso cor="azul" icone="ok">{aviso}</Aviso> : null}
      <Chips valor={status} onChange={v => { setStatus(v); setQuantos(40); }}
        opcoes={[{ k: 'todas', l: 'Todas', n: d.pessoas.length }].concat(ORDEM_STATUS.map(k => ({ k, l: STATUS[k].rp, n: contStatus[k] || 0, cor: STATUS[k].cor })))} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'center' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.apoio }}>
          Papel
          <select value={papel} onChange={e => { setPapel(e.target.value); setQuantos(40); }} style={estiloSelect}>
            <option value="todos">Todos ({fmtN(d.pessoas.length)})</option>
            <option value="professor">Professores ({fmtN(contPapel.professor)})</option>
            <option value="aluno">Alunos ({fmtN(contPapel.aluno)})</option>
            {contPapel.outros ? <option value="outros">Sem papel ({fmtN(contPapel.outros)})</option> : null}
          </select>
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.apoio }}>
          Ordem
          <select value={ordem} onChange={e => setOrdem(e.target.value)} style={estiloSelect}>
            {ORDENS.map(o => <option key={o.k} value={o.k}>{o.l}</option>)}
          </select>
        </label>
      </div>
      <input className="pc-busca" type="search" value={busca} onChange={e => { setBusca(e.target.value); setQuantos(40); }} placeholder="Buscar por nome ou e-mail" aria-label="Buscar pessoa" />
      {aberta && (noTopo || !abertaVisivel) && pessoaAberta ? <div id="pc-ficha">{detalhe(pessoaAberta)}</div> : null}
      <Cartao style={{ padding: '6px 16px' }}>
        <div style={{ fontSize: 12, color: C.apoio, padding: '8px 0' }}>
          {plural(lista.length, 'conta', 'contas')}{d.pessoas.length >= 500 ? ' (a lista mostra no máximo 500)' : ''} · toque numa pessoa para ver o que ela fez
        </div>
        {vis.length ? vis.map(p => (
          <React.Fragment key={p.user_id || p.email || p.nome}>
            <LinhaPessoa p={p} prof={profDe(p.user_id)} aberta={aberta === p.user_id && !noTopo} onClick={() => clicar(p.user_id)} />
            {aberta === p.user_id && !noTopo ? detalhe(p) : null}
          </React.Fragment>
        )) : <Vazio>Ninguém com esses filtros.</Vazio>}
        {lista.length > quantos ? <button type="button" className="pc-link" onClick={() => setQuantos(quantos + 40)}>Mostrar mais ({fmtN(lista.length - quantos)} restantes)</button> : null}
      </Cartao>
    </div>
  );
}

// ERROS: a versao no ar primeiro; as antigas ficam guardadas num <details> fechado
function ItemErro({ e }) {
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid ' + C.linha }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
          <b style={{ fontSize: 13.5 }}>{rotErro(e.tipo)}</b>
          {!num(e.usuarios) ? <Selo bg="#F0F1F4" tx={C.apoio} title="Aconteceu só com quem não estava logado (tela de entrada)">sem login</Selo> : null}
        </div>
        <b className="pc-num" style={{ fontSize: 13.5, whiteSpace: 'nowrap' }}>{fmtN(e.vezes)}×</b>
      </div>
      <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: C.texto, background: C.fundo, borderRadius: 8, padding: '6px 8px', margin: '6px 0', wordBreak: 'break-word' }}>{e.mensagem || '(sem mensagem)'}</div>
      <div style={{ fontSize: 12, color: C.apoio }}>
        {plural(e.usuarios, 'pessoa', 'pessoas')}{e.tela ? ' · em ' + rotTela(e.tela) : ''}{e.versao ? ' · ' + curtaVersao(e.versao) : ''} · última vez {quandoCurto(e.ultima_vez)}
      </div>
    </div>
  );
}
function AbaErros({ d, semEventos, versao }) {
  if (semEventos) return <Cartao><Vazio>{TXT_SEM_EVENTOS}</Vazio></Cartao>;
  const v = versao || versaoMaisRecente(d);
  // primeiro os que aconteceram com alguem logado; os so'-sem-login vao para o fim (a tela de
  // entrada aceita chamada de qualquer um — pode ter mensagem plantada)
  const ordem = (a, b) => (num(b.usuarios) > 0) - (num(a.usuarios) > 0) || num(b.vezes) - num(a.vezes);
  const noAr = d.erros.filter(e => v && e.versao === v).sort(ordem);
  const antigos = d.erros.filter(e => !v || e.versao !== v).sort(ordem);
  // cada tipo de erro leva a versao da ocorrencia MAIS RECENTE: um erro da versao no ar que
  // voltou depois numa versao antiga (app em cache atrasado) fica em "Versoes antigas"
  const nErrosV = v ? num((d.resumo.por_versao.find(x => x && x.versao === v) || {}).erros) : 0;
  const vazioNoAr = nErrosV > 0
    ? 'O banco registrou ' + plural(nErrosV, 'erro', 'erros') + ' nesta versão, mas o mesmo tipo de erro aconteceu depois numa versão antiga: veja em “Versões antigas”.'
    : 'Nenhum erro nesta versão.';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <CaixaGrafico titulo="Erros por dia" observe="Mensagens chegam sem dados pessoais (e-mail, nomes e números são apagados).">
        <Grafico titulo="Erros por dia" itens={d.por_dia} x="dia" rotuloX="Dia" fmtXLongo={fmtDiaLongo} series={[{ k: 'erros', rotulo: 'Erros', cor: C.verm }]} vazio="Nenhum erro no período." />
      </CaixaGrafico>
      <Cartao>
        <Titulo sub="Cada item é um tipo de erro (a mesma mensagem); as vezes somam todas as versões.">{'Versão no ar (' + curtaVersao(v) + ')'}</Titulo>
        <ListaCurta itens={noAr} inicial={12} vazio={vazioNoAr} render={(e, i) => <ItemErro key={'n' + i} e={e} />} />
      </Cartao>
      {antigos.length ? (
        <details className="pc-detalhes">
          <summary className="pc-sum">{'Versões antigas (' + fmtN(antigos.length) + ')'}<span className="pc-sum-seta"><Icone n="baixo" /></span></summary>
          <ListaCurta itens={antigos} inicial={12} render={(e, i) => <ItemErro key={'a' + i} e={e} />} />
        </details>
      ) : null}
    </div>
  );
}

// ---- Erro ao carregar (funcao ausente, sem permissao, sem internet) ----------------
function CaixaErro({ erro, onTentar }) {
  const t = erro.tipo;
  const txt = t === 'ausente'
    ? <><b>O painel ainda não foi instalado no banco.</b><br />Falta rodar o SQL do painel (sql/central_uso.sql) no Supabase — peça ao Claude o código pronto para colar no SQL Editor. Depois disso, toque em “Tentar de novo”.</>
    : t === 'negado'
      ? <b>Só a conta do administrador vê estes números.</b>
      : t === 'rede'
        ? <><b>Sem conexão com o servidor.</b><br />Confira a internet e tente de novo.</>
        : <><b>Não deu para carregar o painel.</b><br /><span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, wordBreak: 'break-word' }}>{erro.msg}</span></>;
  return (
    <Aviso cor={t === 'ausente' ? 'ambar' : 'verm'} icone={t === 'ausente' ? 'banco' : t === 'negado' ? 'cadeado' : 'alerta'}>
      <div>{txt}</div>
      {t !== 'negado' ? <button type="button" className="pc-btn" onClick={onTentar} style={{ marginTop: 10 }}><Icone n="atualizar" /> Tentar de novo</button> : null}
    </Aviso>
  );
}

// ---- Inicio: pecas pequenas -------------------------------------------------------------
function rolarAte(id) {
  try { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
}
// bolinha alinhada com a 1a linha do texto (m = distancia do topo, em "em" da propria fonte)
function Bola({ cor, s, m }) {
  return <span className="pc-bola" aria-hidden="true" style={{ width: s, height: s, background: cor, marginTop: m }} />;
}
function SeloSituacao({ k }) {
  const s = SITUACAO[k] || SITUACAO.emdia;
  return <Selo bg={s.bg} tx={s.tx}>{s.r}</Selo>;
}
// 6 bolinhas: as etapa+1 primeiras cheias (etapa alcancada), as outras vazias
function PontosEtapa({ etapa, peq }) {
  const s = peq ? 6 : 8;
  return (
    <span role="img" aria-label={'Passo ' + (etapa + 1) + ' de 6: ' + ETAPA[etapa]} style={{ display: 'inline-flex', alignItems: 'center', gap: peq ? 2 : 3, flex: '0 0 auto' }}>
      {ETAPA.map((t, i) => (
        <span key={i} style={{ width: s, height: s, borderRadius: '50%', boxSizing: 'border-box', background: i <= etapa ? C.tinta : 'transparent', border: i <= etapa ? 'none' : '1.5px solid ' + C.eixo }} />
      ))}
    </span>
  );
}
// WhatsApp (mensagem pronta) e Copiar e-mail — so' para parado, precisa de ajuda e comecando.
// Os cliques nao chegam ao cartao (que abriria a ficha).
function BotoesContato({ l }) {
  const [estado, setEstado] = React.useState('');   // '' | 'ok' | 'falhou'
  const k = l.sit.k;
  if (k !== 'parado' && k !== 'ajuda' && k !== 'comecando') return null;
  const parar = e => e.stopPropagation();
  const copiar = e => {
    e.stopPropagation();
    let p = null;
    try { p = navigator.clipboard && navigator.clipboard.writeText(l.email); } catch (er) { p = null; }
    if (!p || typeof p.then !== 'function') { setEstado('falhou'); return; }
    p.then(() => { setEstado('ok'); setTimeout(() => setEstado(s => (s === 'ok' ? '' : s)), 2000); }, () => setEstado('falhou'));
  };
  return (
    <div className="pc-contato" onClick={parar}>
      <a className="pc-btn forte peq" href={linkWhats(l.telefone, mensagemDe(l))} target="_blank" rel="noopener noreferrer" onClick={parar}>WhatsApp</a>
      {l.email ? (estado === 'falhou'
        ? <span style={{ alignSelf: 'center', fontSize: 12.5, color: C.texto, userSelect: 'all', wordBreak: 'break-all' }}>{l.email}</span>
        : <button type="button" className="pc-btn peq" onClick={copiar}>{estado === 'ok' ? 'Copiado' : 'Copiar e-mail'}</button>) : null}
    </div>
  );
}
// O mesmo cartao em qualquer largura (Inicio e Detalhes › Professores no celular)
function CartaoProfessor({ l, onAbrir, mostrarTeste }) {
  const clic = !!(l.user_id && onAbrir);
  const abrir = () => { if (clic) onAbrir(l.user_id); };
  const s = SITUACAO[l.sit.k];
  const uso = 'Último uso: ' + ultimoUsoTxt(l);
  const alunos = l.alunos === null ? '— alunos no app' : fmtN(l.noApp) + ' de ' + plural(l.alunos, 'aluno', 'alunos') + ' no app';
  const aulas = l.aulas7 === null ? '— aulas em 7 dias' : plural(l.aulas7, 'aula', 'aulas') + ' em 7 dias' + (l.aulas7antes !== null ? ' (antes: ' + fmtN(l.aulas7antes) + ')' : '');
  // sem nenhum aluno, "0 de 0 alunos · 0 aulas" nao diz nada: a tarefa ja' esta' no proximo passo
  const semAlunos = l.alunos === 0 && !num(l.aulas7) && !num(l.aulas7antes);
  const linhaNums = [uso].concat(semAlunos ? [] : [alunos, aulas]).join(' · ');
  return (
    <div className={'pc-prof' + (clic ? ' clic' : '')} onClick={clic ? abrir : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        {clic
          ? <button type="button" className="pc-prof-nome" onClick={e => { e.stopPropagation(); abrir(); }} aria-label={'Abrir a ficha de ' + l.nome}>{l.nome}</button>
          : <span className="pc-prof-nome" style={{ cursor: 'default' }}>{l.nome}</span>}
        <span style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, flex: '0 0 auto' }}>
          {mostrarTeste && l.interno ? <Selo bg="#E9EBEF" tx={C.tinta}>Teste</Selo> : null}
          <SeloSituacao k={l.sit.k} />
        </span>
      </div>
      {l.sit.k !== 'emdia' && l.sit.motivo ? <div style={{ fontSize: 12.5, color: s.tx, lineHeight: 1.4 }}>{l.sit.motivo}</div> : null}
      <div style={{ fontSize: 13.5, fontWeight: 700, color: l.sit.cinza ? C.apoio : C.tinta, lineHeight: 1.35 }}>Próximo passo: {l.sit.passo}</div>
      <div style={{ fontSize: 12.5, color: C.apoio, lineHeight: 1.4 }}>{linhaNums}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.texto }}>
        {l.etapa === null ? <span>—</span> : <><PontosEtapa etapa={l.etapa} /><span>{ETAPA[l.etapa]}</span></>}
      </div>
      <BotoesContato l={l} />
    </div>
  );
}

// Bloco escuro: a conclusao do dia
function HeroiPlen({ est, irPara }) {
  return (
    <section className="pc-heroi" aria-label="Resumo do dia">
      <div className="pc-heroi-cab">
        <h2 className="pc-heroi-tit" style={{ margin: 0 }}>
          <Bola cor={est.bolinha} s={10} m="calc(0.65em - 5px)" />
          <span>
            {est.titulo}
            {est.ver ? <> <button type="button" className="pc-link inl claro" onClick={() => rolarAte('pc-professores')}>ver</button></> : null}
          </span>
        </h2>
        <div className="pc-heroi-data">{fmtHojeLongo()}</div>
      </div>
      {est.frases.length ? (
        <ul className="pc-heroi-frases">
          {est.frases.map((f, i) => (
            <li key={i} className="pc-heroi-frase">
              <Bola cor={f.cor || C.dica} s={6} m="calc(0.725em - 3px)" />
              <span>
                {f.t}
                {f.ir ? <> — <button type="button" className="pc-link inl claro" onClick={() => irPara(f.ir)}>ver</button></> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

// Mini-colunas (sparkline) das ultimas semanas — so' no cartao de aulas
function Barrinhas({ sr }) {
  const l = sr.ult8;
  if (l.length < 3) return null;
  const max = Math.max(1, ...l.map(s => num(s.aulas_dadas)));
  const W = 12, G = 5, H = 36;
  const larg = l.length * W + (l.length - 1) * G;
  const desc = l.map(s => fmtSemanaCurta(s) + (s === sr.atual ? ' (até agora)' : '') + ': ' + fmtN(s.aulas_dadas)).join('; ');
  return (
    <svg className="pc-barrinhas" width={larg} viewBox={'0 0 ' + larg + ' ' + H} preserveAspectRatio="none" role="img"
      aria-label={'Aulas registradas por semana: ' + desc} style={{ flex: '0 0 auto' }}>
      {l.map((s, i) => {
        const v = num(s.aulas_dadas);
        const ehAtual = s === sr.atual, ehRef = s === sr.ref;
        const x = i * (W + G);
        const tit = <title>{'Semana de ' + fmtSemanaCurta(s) + ': ' + plural(v, 'aula', 'aulas') + (ehAtual ? ' (até agora)' : '')}</title>;
        if (v === 0) return <rect key={i} x={x} y={H - 2} width={W} height={2} fill={ehRef ? C.tinta : ehAtual ? C.apoio : C.eixo}>{tit}</rect>;
        const h = Math.max(3, (v / max) * (H - 1));
        return ehAtual
          ? <rect key={i} x={x + 0.75} y={H - h + 0.75} width={W - 1.5} height={h - 1.5} rx="2" fill="#fff" stroke={C.apoio} strokeWidth="1.5" strokeDasharray="3 2" vectorEffect="non-scaling-stroke">{tit}</rect>
          : <rect key={i} x={x} y={H - h} width={W} height={h} rx="2" fill={ehRef ? C.tinta : C.eixo}>{tit}</rect>;
      })}
    </svg>
  );
}
// Cartao de numero: o cartao inteiro e' um botao (abre o assunto ou rola ate' a lista). Sem o
// icone (i): no celular, tocar nele abria o assunto em vez de explicar; o title fica como extra.
// Valor comprido (dinheiro com centavos) usa letra menor e pode quebrar depois do "R$".
function CartaoKpi({ rotulo, etiqueta, valor, de, linhas, dica, extra, onClick }) {
  const ls = arr(linhas).filter(Boolean).slice(0, 2);
  const longo = String(valor).length >= 10;
  return (
    <button type="button" className="pc-kpi" onClick={onClick} aria-label={rotulo + ': ' + valor + (de ? ' ' + de : '') + ', ' + etiqueta}>
      <span className="pc-kpi-topo">
        <span className="pc-kpi-rot" title={dica}>{rotulo}</span>
        <span className="pc-kpi-etq">{etiqueta}</span>
      </span>
      <span className="pc-kpi-meio">
        <span className={'pc-kpi-valor pc-num' + (longo ? ' longo' : '')}>{valor}{de ? <span className="pc-kpi-de">{' ' + de}</span> : null}</span>
        {extra || null}
      </span>
      {ls.map((t, i) => <span key={i} className="pc-kpi-linha">{t}</span>)}
    </button>
  );
}

// Lista de professores: "Precisa de voce", "Comecando", "Em dia" e "Sem dados agora" (urgente
// nunca fica escondido). Grupos um embaixo do outro, com o subtitulo na linha toda; no
// computador, quando todo grupo tem ate' 2 cartoes, viram colunas lado a lado (senao a faixa
// "O app" nao cabe sem rolar, criterio 19). Com mais cartoes, empilhar mostra mais urgentes
// acima da dobra que uma coluna comprida.
function ListaProfessores({ linhas, piloto, abrirPessoa }) {
  const [todos, setTodos] = React.useState(false);
  const g = agrupar(linhas);
  const cartao = l => <CartaoProfessor key={l.chave} l={l} onAbrir={uid => abrirPessoa(uid, { de: 'inicio' })} />;
  const grupos = [
    { k: 'urg', t: 'Precisa de você', l: g.urg },
    { k: 'com', t: 'Começando', l: g.com },
    { k: 'ok', t: 'Em dia', l: todos ? g.ok : g.ok.slice(0, 6), total: g.ok.length },
    { k: 'sem', t: 'Sem dados agora', l: g.sem },
  ].filter(x => (x.total != null ? x.total : x.l.length) > 0);
  const lado = grupos.length > 1 && grupos.every(x => x.l.length <= 2);
  return (
    <section id="pc-professores" aria-label="Professores">
      <Titulo>{'Professores (' + fmtN(linhas.length) + ')'}</Titulo>
      {!piloto ? (
        <div style={{ marginBottom: 12 }}>
          <Aviso cor="ambar" icone="alerta">Não deu para ler alunos, aulas e etapa de cada professor agora. Toque em Atualizar.</Aviso>
        </div>
      ) : null}
      {!linhas.length ? (
        <Cartao><Vazio>Nenhum professor ainda. Quem criar uma conta de professor aparece aqui (os professores de teste ficam fora).</Vazio></Cartao>
      ) : (
        <div className={'pc-grupos' + (lado ? ' lado' : '')} style={{ '--n': grupos.length }}>
          {grupos.map(gr => (
            <div key={gr.k} className="pc-grupo-col">
              <div className="pc-grupo">{gr.t + ' (' + fmtN(gr.total != null ? gr.total : gr.l.length) + ')'}</div>
              <div className="pc-profs">{gr.l.map(cartao)}</div>
              {gr.k === 'ok' && gr.total > 6 ? (
                <div><button type="button" className="pc-link" onClick={() => setTodos(!todos)}>{todos ? 'Mostrar menos' : 'Ver todos (' + fmtN(gr.total) + ')'}</button></div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const errosDaVersao = (d, v) => d.erros.filter(e => v && e.versao === v && num(e.usuarios) > 0);
// Faixa "O app": uma linha; erro so' da versao no ar (as antigas nunca aparecem no Inicio).
// "nenhum erro nela" so' quando a contagem por versao (resumo.por_versao[v].erros) tambem e' 0:
// a lista de erros guarda cada tipo na versao da ocorrencia MAIS RECENTE, e um erro da versao no
// ar que voltou depois numa versao antiga sairia dela.
function FaixaApp({ base, v, irPara }) {
  const pv = base.resumo.por_versao.find(x => x && x.versao === v) || {};
  const usam = num(pv.usuarios), nErrosV = num(pv.erros);
  const erros = errosDaVersao(base, v);
  const com = erros.length > 0;
  return (
    <button type="button" className="pc-faixa" onClick={() => irPara('erros')} style={{ color: com ? C.vermEsc : C.apoio }}>
      <Icone n={com ? 'erro' : 'ok'} c={com ? C.verm : C.apoio} />
      <span style={{ minWidth: 0 }}>
        {com
          ? 'App ' + curtaVersao(v) + ': ' + plural(erros.length, 'tipo de erro', 'tipos de erro') + ' nesta versão — ver'
          : 'App ' + curtaVersao(v) + ' no ar · ' + (usam > 0 ? 'usado por ' + plural(usam, 'pessoa', 'pessoas') + ' nos últimos 30 dias' : 'ninguém abriu esta versão ainda')
            + ' · ' + (nErrosV > 0 ? plural(nErrosV, 'erro registrado', 'erros registrados') + ' nela — ver' : 'nenhum erro nela')}
      </span>
    </button>
  );
}
function LinksDetalhes({ irPara }) {
  return (
    <nav aria-label="Mais detalhes">
      <div style={{ fontFamily: F_TIT, fontSize: 13.5, fontWeight: 700, color: C.tinta, marginBottom: 8 }}>Mais detalhes</div>
      <div className="pc-links">
        {SECOES.map(s => <button key={s.k} type="button" className="pc-chip" onClick={() => irPara(s.k)}>{s.lk}</button>)}
      </div>
    </nav>
  );
}
function ComoContamos({ fase2 }) {
  const itens = [
    ['Aula registrada', 'aula marcada no app como dada ou falta, pelo professor ou pelo aluno. Se só um dos dois marcou, ela fecha sozinha depois de 24 horas. Conta pela data da aula.'],
    ['Semana', 'de segunda a domingo, no horário de Brasília. "Semana passada" é a última semana completa.'],
    ['Professor dando aula', 'tem pelo menos 1 aula registrada ' + (fase2 ? 'nos últimos 7 dias, no horário de Brasília' : 'hoje ou nos 7 dias anteriores') + '. É a mesma conta do "aulas em 7 dias" de cada professor, por isso pode diferir do cartão de aulas, que olha a última semana completa.'],
    ['Último uso', 'a última vez que o professor entrou no app, mexeu nele ou cadastrou algo. Aula ou cobrança criada sozinha pela agenda não conta.'],
    ['Aluno no app', fase2 ? 'ficha de aluno ativa ligada a uma conta (sem as contas de teste).' : 'ficha de aluno ligada a uma conta. Pode contar a mais quando há ficha desvinculada com conta.'],
    ['Recebido pelos professores', 'pagamento marcado como recebido (Pix, dinheiro ou outro), pela data do pagamento. O saldo antigo lançado no começo não entra. O dinheiro vai direto do aluno para o professor.'],
    ['Situação do professor', 'Parado = nunca voltou depois do cadastro, ou está há 7 dias ou mais sem usar; Precisa de ajuda = usa, mas travou num passo, sumiu de repente (5 ou 6 dias) ou ficou sem registrar aula; Começando = conta com menos de 3 dias; Em dia = o resto; Sem dados agora = não deu para ler alunos, aulas e etapa dele.'],
    ['Contas de teste', 'os professores de teste (a demo da Apple e o seu professor de teste) ficam fora de tudo nesta página. A exceção é a sua conta de aluno ligada a um professor de verdade: ela conta ' + (fase2 ? 'nas aulas dele.' : 'em "Alunos no app" e nas aulas dele.')],
  ];
  return (
    <details className="pc-detalhes">
      <summary className="pc-sum">Como contamos<span className="pc-sum-seta"><Icone n="baixo" /></span></summary>
      <ul style={{ margin: '2px 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: C.texto, lineHeight: 1.5 }}>
        {itens.map(([t, x]) => <li key={t}><b style={{ color: C.tinta }}>{t}:</b> {x}</li>)}
      </ul>
    </details>
  );
}

// Esqueleto da primeira carga: mesmo tamanho do Inicio pronto (o layout nao pula)
function EsqueletoInicio() {
  const barra = w => <div style={{ height: 12, width: w, borderRadius: 6, background: 'rgba(255,255,255,.14)' }} />;
  return (
    <div className="pc-inicio" style={{ display: 'flex', flexDirection: 'column', gap: 14 }} aria-busy="true">
      <section className="pc-heroi pc-esq-heroi">
        <div className="pc-heroi-tit">Carregando os números…</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>{barra('70%')}{barra('52%')}</div>
      </section>
      <div className="pc-kpis">{[0, 1, 2, 3].map(i => <div key={i} className="pc-esq pc-esq-kpi" />)}</div>
      <div>
        {/* lugar do titulo "Professores (n)" e do rotulo do grupo */}
        <div className="pc-esq" style={{ height: 20, width: 140, marginBottom: 34 }} />
        <div className="pc-profs">{[0, 1].map(i => <div key={i} className="pc-esq" style={{ height: 156 }} />)}</div>
      </div>
    </div>
  );
}

// ---- Indicações (v144): professores que entraram pelo convite de outro professor ----------
// painel_indicacoes (sql/v144ind.sql). Busca sozinho (como a ficha de Pessoas). Banco sem a função (antes do SQL)
// ou sem internet: o Início não mostra nada e Crescimento avisa. Contas de teste ficam fora, a não ser com
// "Incluir contas de teste" (internos). compacto = a linha do Início, que só aparece com 1 indicação ou mais.
function BlocoIndicacoes({ supabase, internos = false, compacto = false, giro = 0, irPara, abrirPessoa }) {
  const [r, setR] = React.useState(null);   // null = carregando · { dados } · { erro }
  React.useEffect(() => {
    if (!supabase) return undefined;
    let vivo = true;
    Promise.resolve().then(() => supabase.rpc('painel_indicacoes', { p_incluir_internos: !!internos }))
      .then(res => { if (vivo) setR(res && !res.error && lerJson(res.data) ? { dados: lerJson(res.data) } : { erro: true }); },
            () => { if (vivo) setR({ erro: true }); });
    return () => { vivo = false; };
  }, [supabase, internos, giro]);
  const x = r && r.dados;
  const n = x ? num(x.por_indicacao) : 0;
  const tot = x ? num(x.total_professores) : 0;
  const inds = x ? arr(x.indicadores) : [];
  const lista = x ? arr(x.professores) : [];
  const quem = i => (i && i.nome ? primeiroNome(i.nome) : 'conta excluída');
  const topo = inds.slice(0, 3).map(i => quem(i) + ' trouxe ' + fmtN(i.n)).join(' · ');
  const numero = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px' }}>
      <span className="pc-num" style={{ fontFamily: F_TIT, fontSize: 24, fontWeight: 800, color: C.tinta, lineHeight: 1.1 }}>
        {fmtN(n)}{tot ? <span style={{ fontSize: 14, fontWeight: 600, color: C.apoio }}>{' de ' + fmtN(tot)}</span> : null}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: C.tinta }}>{n === 1 ? 'professor entrou' : 'professores entraram'} pelo convite de outro professor</span>
    </div>
  );
  if (compacto) {
    if (!x || !n) return null;
    return (
      <Cartao>
        {numero}
        <div style={{ fontSize: 12.5, color: C.apoio, marginTop: 6, lineHeight: 1.5 }}>
          {topo}
          {irPara ? <>{topo ? ' · ' : ''}<button type="button" className="pc-link" style={{ fontSize: 12.5, padding: '0 2px', verticalAlign: 'baseline' }} onClick={() => irPara('crescimento')}>Ver quem</button></> : null}
        </div>
      </Cartao>
    );
  }
  return (
    <Cartao>
      <Titulo sub={'Quem criou a conta pelo link "Convidar outro professor" de outro professor (' + (internos ? 'com' : 'sem') + ' as contas de teste).'}>Crescimento por indicação</Titulo>
      {!r ? <Vazio>Carregando…</Vazio>
        : !x ? <Vazio>Não deu para ler as indicações agora. Se o SQL da v144 ainda não rodou no banco, elas aparecem depois dele.</Vazio>
          : !n ? <Vazio>Ninguém entrou por indicação ainda. Quando um professor criar a conta pelo link de outro, aparece aqui.</Vazio>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {numero}
                {topo ? <div style={{ fontSize: 13, color: C.texto }}>{topo}</div> : null}
                <div>
                  {lista.map(p => (
                    <ItemClicavel key={p.professor_id} onClick={p.user_id && abrirPessoa ? () => abrirPessoa(p.user_id) : null}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 8px' }}>
                        <b style={{ color: C.tinta, fontSize: 14 }}>{p.nome || 'Professor'}</b>
                        {p.interno ? <Selo bg="#E9EBEF" tx={C.tinta}>Teste</Selo> : null}
                      </div>
                      <div style={{ fontSize: 12.5, color: C.apoio, marginTop: 2 }}>
                        {'indicado por ' + quem({ nome: p.indicador_nome }) + ' · entrou ' + quando(p.indicado_em) + ' (' + fmtData(p.indicado_em) + ')'}
                      </div>
                    </ItemClicavel>
                  ))}
                </div>
              </div>
            )}
    </Cartao>
  );
}

// ---- INICIO (exportado tambem para o teste: recebe os dados ja' carregados) ----------------
export function AbaInicio({ base, piloto, premios, extra, versaoNoAr, email, irPara, abrirPessoa, verContasTeste, supabase, giro }) {
  const cart = React.useMemo(() => montarCarteira(piloto, base, extra), [piloto, base, extra]);
  const linhas = cart.linhas;
  const sr = React.useMemo(() => semanasRef(base.crescimento.semanas, base.gerado_em), [base]);
  const v = versaoDoPainel(base, versaoNoAr);
  const errosNoAr = errosDaVersao(base, v);
  const ids = {};
  linhas.forEach(l => { if (l.profId) ids[l.profId] = true; });
  const premiosReais = arr(premios).filter(r => r && ids[r.professor_id]);
  const est = estadoPlen({ linhas, sr, errosNoAr, v, premiosReais, premiosTodos: arr(premios).length });
  const fase2 = linhas.some(l => l.fase2);

  // 1. aulas registradas (semana fechada; sem ela, a semana corrente)
  const k1 = sr.ref
    ? { valor: fmtN(sr.ref.aulas_dadas), etiqueta: 'semana passada',
        linhas: [sr.ant ? compara(sr.ref.aulas_dadas, sr.ant.aulas_dadas, 'aula', 'aulas') : 'primeira semana completa do piloto', 'Esta semana até agora: ' + fmtN(sr.atual && sr.atual.aulas_dadas)] }
    : { valor: fmtN(sr.atual && sr.atual.aulas_dadas), etiqueta: 'esta semana', linhas: ['primeira semana do piloto'] };
  // 2 e 3 contam so' o que se sabe: professor sem dados (linha parcial) nunca vira zero
  const n = linhas.length;
  const parciais = linhas.filter(l => l.etapa === null).length;
  const semDadoTxt = parciais ? 'sem dados de ' + plural(parciais, 'professor', 'professores') : null;
  // 2. professores dando aula (aula registrada em 7 dias)
  const comAula = linhas.filter(l => num(l.aulas7) > 0);
  const k2 = !piloto || (n && parciais === n) ? { valor: '—', linhas: ['Não deu para ler agora'] }
    : !n ? { valor: '—', linhas: ['Nenhum professor ainda'] }
      : { valor: fmtN(comAula.length), de: 'de ' + fmtN(n),
          linhas: [comAula.length ? nomesOuContagem(comAula.map(l => primeiroNome(l.nome)), 3, null) : parciais ? null : 'Nenhum registrou aula', semDadoTxt] };
  // 3. alunos no app = soma dos "X de Y" dos cartoes
  const totAlunos = linhas.reduce((a, l) => a + num(l.alunos), 0);
  const totApp = linhas.reduce((a, l) => a + num(l.noApp), 0);
  const falta = totAlunos - totApp;
  const k3 = !piloto || (n && parciais === n) ? { valor: '—', linhas: ['Não deu para ler agora'] }
    : !totAlunos ? { valor: '—', linhas: [parciais ? semDadoTxt : 'Nenhum aluno cadastrado ainda'] }
      : { valor: fmtN(totApp), de: 'de ' + fmtN(totAlunos), linhas: [falta > 0 ? fmtN(falta) + ' ainda não ' + (falta === 1 ? 'entrou' : 'entraram') : 'Todos já entraram', semDadoTxt] };
  // 4. recebido pelos professores neste mes (sem saldo migrado, sem professor de teste)
  const meses = base.crescimento.meses;
  const mAt = meses.length ? meses[meses.length - 1] : null;
  const mAnt = meses.length > 1 ? meses[meses.length - 2] : null;
  const nomeMes = m => { const x = /^(\d{4})-(\d{2})/.exec(String((m && m.mes) || '')); const t = x ? MESES_LONGOS[+x[2] - 1] : ''; return t.charAt(0).toUpperCase() + t.slice(1); };
  const k4linhas = soma(base.crescimento.semanas, 'gmv_recebido') === 0
    ? ['Nenhum pagamento registrado ainda — normal até o primeiro pacote fechar.']
    : mAnt ? [nomeMes(mAnt) + ': ' + fmtR(mAnt.gmv_recebido)] : [];

  return (
    <div className="pc-inicio" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HeroiPlen est={est} irPara={irPara} />
      <div className="pc-kpis">
        <CartaoKpi rotulo="Aulas registradas" etiqueta={k1.etiqueta} valor={k1.valor} linhas={k1.linhas}
          dica="Aula marcada no app como dada ou falta, pela data da aula." extra={<Barrinhas sr={sr} />} onClick={() => irPara('crescimento')} />
        <CartaoKpi rotulo="Professores dando aula" etiqueta="7 dias" valor={k2.valor} de={k2.de} linhas={k2.linhas}
          dica={fase2 ? 'Professor com pelo menos 1 aula registrada nos últimos 7 dias (horário de Brasília).' : 'Professor com pelo menos 1 aula registrada hoje ou nos 7 dias anteriores.'}
          onClick={() => rolarAte('pc-professores')} />
        {/* abre Detalhes › Professores: a coluna "Alunos (no app)" soma exatamente este numero
            (a lista de Pessoas › Alunos e' de contas e deixa a conta de aluno de teste de fora) */}
        <CartaoKpi rotulo="Alunos no app" etiqueta="hoje" valor={k3.valor} de={k3.de} linhas={k3.linhas}
          dica={fase2 ? 'Ficha de aluno ativa ligada a uma conta no app (sem as contas de teste).' : 'Ficha de aluno ligada a uma conta no app. Pode contar a mais quando há ficha desvinculada com conta.'}
          onClick={() => irPara('professores')} />
        <CartaoKpi rotulo="Recebido pelos professores" etiqueta="este mês" valor={fmtRq(mAt && mAt.gmv_recebido)} linhas={k4linhas}
          dica="Pagamentos que os professores marcaram como recebidos (Pix, dinheiro ou outro), pela data do pagamento. O dinheiro vai direto para o professor. Saldo antigo lançado no começo não conta."
          onClick={() => irPara('crescimento')} />
      </div>
      {supabase ? <BlocoIndicacoes supabase={supabase} compacto giro={giro} irPara={irPara} /> : null}
      <ListaProfessores linhas={linhas} piloto={piloto} abrirPessoa={abrirPessoa} />
      <FaixaApp base={base} v={v} irPara={irPara} />
      <LinksDetalhes irPara={irPara} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ComoContamos fase2={fase2} />
        <div style={{ fontSize: 12, color: C.apoio, lineHeight: 1.5 }}>
          {plural(base.contas.internas, 'conta de teste', 'contas de teste')} (a demo da Apple e as suas) · os professores de teste ficam fora do Início ·{' '}
          <button type="button" className="pc-link" style={{ fontSize: 12, padding: '0 2px', verticalAlign: 'middle' }} onClick={verContasTeste}>Ver contas de teste</button>
        </div>
        <div style={{ fontSize: 12, color: C.apoio }}>Só números de uso, sem conteúdo (nada do que as pessoas digitam). Visível apenas para o administrador.</div>
        {email ? <div style={{ fontSize: 12, color: C.apoio, wordBreak: 'break-all' }}>Entrou como {email}</div> : null}
      </div>
    </div>
  );
}

// ---- DETALHES › Professores: degraus + tabela completa (cartoes no celular) ---------------
const ORDENS_PROF = [['precisa', 'Quem precisa de você'], ['aulas', 'Mais aulas'], ['nome', 'Nome']];
function ordenarCarteira(linhas, ordem) {
  if (ordem === 'aulas') return linhas.slice().sort((a, b) => num(b.aulas7) - num(a.aulas7) || num(b.aulas30) - num(a.aulas30) || String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  if (ordem === 'nome') return linhas.slice().sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  const g = agrupar(linhas);
  return g.urg.concat(g.com, g.ok, g.sem);
}
function ProfessoresDetalhe({ carteira, piloto, abrirPessoa, mostrarTeste }) {
  const [ordem, setOrdem] = React.useState('precisa');
  const linhas = carteira.linhas;
  const fase2 = linhas.some(l => l.fase2);
  const lista = ordenarCarteira(linhas, ordem);
  const degraus = ['Criou a conta', 'Cadastrou aluno', 'Convidou aluno', 'Aluno entrou no app', 'Registrou aula', 'Registrou pagamento']
    .map((r, k) => ({ chave: 'e' + k, rotulo: r, n: linhas.filter(l => l.etapa !== null && l.etapa >= k).length, fim: k === 5 }));
  const ultimo = ultimoUsoTxt;
  const colunas = [
    { t: 'Professor', c: l => <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}><b style={{ color: C.tinta }}>{l.nome}</b>{mostrarTeste && l.interno ? <Selo bg="#E9EBEF" tx={C.tinta}>Teste</Selo> : null}</span> },
    { t: 'Situação', c: l => <SeloSituacao k={l.sit.k} /> },
    { t: 'Caminho', c: l => (l.etapa === null ? '—' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><PontosEtapa etapa={l.etapa} />{ETAPA[l.etapa]}</span>) },
    { t: 'Alunos (no app)', d: true, c: l => (l.alunos === null ? '—' : fmtN(l.alunos) + ' (' + fmtN(l.noApp) + ')') },
    { t: 'Convites esperando', d: true, c: l => nOuTraco(l.convites) },
    { t: 'Acordos ativos', d: true, c: l => nOuTraco(l.acordos) },
    { t: 'Aulas 7 dias', d: true, c: l => nOuTraco(l.aulas7) },
    { t: 'Aulas 30 dias', d: true, c: l => nOuTraco(l.aulas30) },
    { t: 'Em aberto', d: true, c: l => (l.pendente === null ? '—' : fmtR(l.pendente)) },
    { t: fase2 ? 'Recebido desde o início' : 'Recebido desde o início*', d: true, c: l => (l.recebidoTotal === null ? '—' : fmtR(l.recebidoTotal)) },
    { t: 'Último uso', c: ultimo },
  ].concat(fase2 ? [{ t: 'Recebido no mês', d: true, c: l => (l.recebidoMes === null ? '—' : fmtR(l.recebidoMes)) }] : []);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!piloto ? <Aviso cor="ambar" icone="alerta">Não deu para ler alunos, aulas e etapa de cada professor agora. Toque em Atualizar.</Aviso> : null}
      <Cartao>
        <Titulo sub={'Quantos professores já passaram por cada etapa (' + (mostrarTeste ? 'com' : 'sem') + ' os professores de teste).'}>Até onde cada professor chegou</Titulo>
        {linhas.some(l => l.etapa !== null) ? <Degraus degraus={degraus} /> : <Vazio>Ainda não há professores com etapa para mostrar.</Vazio>}
      </Cartao>
      <Cartao>
        <Titulo>{'Professores (' + fmtN(linhas.length) + ')'}</Titulo>
        {/* a ordem fica embaixo do titulo: ao lado, em 375 px, ela espremia o titulo e passava da borda */}
        <div style={{ marginBottom: 10 }}><Segmentado rotulo="Ordem" valor={ordem} onChange={setOrdem} opcoes={ORDENS_PROF} /></div>
        {!linhas.length ? <Vazio>Nenhum professor ainda.</Vazio> : (
          <>
            <div className="pc-so-largo pc-rolagem">
              <table className="pc-tabela pc-tab-prof" style={{ minWidth: 980 }}>
                <thead><tr>{colunas.map(c => <th key={c.t} className={c.d ? 'd' : undefined}>{c.t}</th>)}</tr></thead>
                <tbody>
                  {lista.map(l => (
                    <tr key={l.chave} onClick={l.user_id ? () => abrirPessoa(l.user_id) : undefined}
                      tabIndex={l.user_id ? 0 : undefined} onKeyDown={l.user_id ? (e => { if (e.key === 'Enter') abrirPessoa(l.user_id); }) : undefined}
                      style={{ cursor: l.user_id ? 'pointer' : 'default' }}>
                      {colunas.map(c => <td key={c.t} className={c.d ? 'd pc-num' : undefined} style={c.d ? { whiteSpace: 'nowrap' } : undefined}>{c.c(l)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pc-so-estreito pc-profs">
              {lista.map(l => <CartaoProfessor key={l.chave} l={l} onAbrir={abrirPessoa} mostrarTeste={mostrarTeste} />)}
            </div>
            <div style={{ fontSize: 12, color: C.apoio, marginTop: 10, lineHeight: 1.45 }}>
              {fase2 ? '' : '* pode incluir saldo antigo lançado no começo; o cartão do Início não inclui. '}Em aberto inclui parcelas que ainda vão vencer.
            </div>
          </>
        )}
      </Cartao>
    </div>
  );
}

// ---- DETALHES: chips de assunto, controles que valem para o assunto e o conteudo ---------
const SECOES_COM_EVENTOS = ['acessos', 'telas', 'desistencias', 'erros'];
function VistaDetalhes(props) {
  const { secao, irPara, d, carregando, erro, onTentar, piloto, extra, dias, setDias, pode90, internos, setInternos,
    supabase, alvo, onMudouPessoa, avisoPessoas, promocoes, versaoNoAr, abrirPessoa, onAlvoUsado } = props;
  const sec = SECOES.find(s => s.k === secao) || SECOES[0];
  const carteira = React.useMemo(() => (d ? montarCarteira(piloto, d, extra) : { linhas: [], escondidos: 0 }), [piloto, d, extra]);
  const mapa = React.useMemo(() => { const m = {}; carteira.linhas.forEach(l => { if (l.user_id) m[l.user_id] = l; }); return m; }, [carteira]);
  const semEventos = !!d && soma(d.por_dia, 'eventos') === 0 && num(d.resumo.sessoes) === 0;
  const iniMed = d ? (d.crescimento.semanas.find(s => s.ativos_semana != null) || {}).semana || null : null;

  let conteudo = null;
  if (sec.k === 'promocoes') conteudo = promocoes || <Cartao><Vazio>As promoções não estão disponíveis aqui.</Vazio></Cartao>;
  else if (!d) conteudo = erro ? <CaixaErro erro={erro} onTentar={onTentar} /> : <Cartao><Vazio>Carregando os números…</Vazio></Cartao>;
  else if (sec.k === 'professores') conteudo = <ProfessoresDetalhe carteira={carteira} piloto={piloto} abrirPessoa={abrirPessoa} mostrarTeste={internos} />;
  else if (sec.k === 'pessoas') conteudo = <AbaPessoas d={d} supabase={supabase} alvo={alvo} onMudou={onMudouPessoa} aviso={avisoPessoas} carteira={mapa} onAlvoUsado={onAlvoUsado} />;
  else if (sec.k === 'crescimento') conteudo = <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}><BlocoIndicacoes supabase={supabase} internos={internos} abrirPessoa={abrirPessoa} /><TabelaSemanas semanas={d.crescimento.semanas} geradoEm={d.gerado_em} /><AbaCrescimento d={d} /></div>;
  else if (sec.k === 'acessos') conteudo = <AbaAcessos d={d} semEventos={semEventos} iniMed={iniMed} versao={versaoNoAr} />;
  else if (sec.k === 'telas') conteudo = <AbaUso d={d} semEventos={semEventos} />;
  else if (sec.k === 'desistencias') conteudo = <AbaFunis d={d} semEventos={semEventos} />;
  else if (sec.k === 'erros') conteudo = <AbaErros d={d} semEventos={semEventos} versao={versaoNoAr} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="pc-chips-sec">
        <Chips opcoes={SECOES.map(s => ({ k: s.k, l: s.l }))} valor={sec.k} onChange={k => irPara(k)} />
      </div>
      {sec.periodo || sec.internos ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px' }}>
          {sec.periodo ? <Segmentado rotulo="Período" valor={dias} onChange={setDias} opcoes={[[7, '7 dias'], [30, '30 dias']].concat(pode90 ? [[90, '90 dias']] : [])} /> : null}
          {sec.internos ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: C.texto, cursor: 'pointer', minHeight: 44, padding: '0 4px' }}>
              <input type="checkbox" checked={internos} onChange={e => setInternos(e.target.checked)} style={{ width: 18, height: 18, accentColor: C.azul, margin: 0 }} />
              Incluir contas de teste
            </label>
          ) : null}
          {carregando && d ? <span style={{ fontSize: 12.5, color: C.apoio }}>Atualizando…</span> : null}
        </div>
      ) : null}
      {internos && sec.internos ? <Aviso cor="azul" icone="info">Mostrando contas de teste (a demo da Apple e as suas). O Início continua sem os professores de teste.</Aviso> : null}
      {d && erro && sec.k !== 'promocoes' ? <Aviso cor="ambar" icone="alerta">Não deu para atualizar estes números agora.</Aviso> : null}
      {semEventos && SECOES_COM_EVENTOS.indexOf(sec.k) >= 0 ? (
        <Aviso cor="azul" icone="info"><b>{TXT_SEM_EVENTOS}</b> Enquanto isso, Professores, Pessoas e Crescimento já mostram o histórico (vem dos cadastros, aulas e pagamentos).</Aviso>
      ) : null}
      <div key={sec.k} className="pc-entra">{conteudo}</div>
    </div>
  );
}

// Linha 2: no Inicio "Atualizado às…  [Atualizar]  ·  Detalhes →"; em Detalhes "← Inicio  ·  Atualizado às… [Atualizar]"
function LinhaTopo({ vista, gerado, carregando, onAtualizar, onDetalhes, onInicio }) {
  const atual = (
    <div className="pc-topo-esq">
      <span style={{ fontSize: 12.5, color: C.apoio }}>{gerado ? 'Atualizado às ' + fmtHora(gerado) : 'Carregando…'}</span>
      <button type="button" className="pc-btn pc-atualizar" onClick={onAtualizar} disabled={carregando} aria-label={carregando ? 'Atualizando' : 'Atualizar'}>
        <Icone n="atualizar" /><span className="pc-atualizar-txt">{carregando ? 'Atualizando…' : 'Atualizar'}</span>
      </button>
    </div>
  );
  if (vista === 'detalhes') {
    return (
      <div className="pc-topo">
        <button type="button" className="pc-link" onClick={onInicio}>← Início</button>
        {atual}
      </div>
    );
  }
  return (
    <div className="pc-topo">
      {atual}
      {onDetalhes ? <button type="button" className="pc-link" onClick={onDetalhes}>Detalhes →</button> : null}
    </div>
  );
}

// ---- O painel ------------------------------------------------------------------------
// versaoNoAr = VERSAO_APP do app.jsx; email = quem entrou (rodape); promocoes = o
// <AdminPromocoes/> do app.jsx; onAba(k) = 'inicio' | 'pessoas' | 'detalhes', chamado quando a
// vista ou o assunto MUDA (a montagem ja' e' registrada no app.jsx).
export function PainelCentral({ supabase, versaoNoAr, email, promocoes, onAba }) {
  const prefs0 = React.useMemo(lerPrefs, []);
  const [dias, setDias] = React.useState([7, 30, 90].indexOf(prefs0.dias) >= 0 ? prefs0.dias : 30);
  const [secao, setSecao] = React.useState(SECOES.some(s => s.k === prefs0.secao) ? prefs0.secao : 'professores');
  const [vista, setVista] = React.useState('inicio');   // sempre abre no Inicio
  const [internos, setInternos] = React.useState(false); // nunca guardado
  const [base, setBase] = React.useState(null);
  const [piloto, setPiloto] = React.useState(null);
  const [premios, setPremios] = React.useState([]);
  const [extraBase, setExtraBase] = React.useState(null);
  const [erro, setErro] = React.useState(null);
  const [carregando, setCarregando] = React.useState(true);
  const [det, setDet] = React.useState(null);           // { chave, dados, extra }
  const [detErro, setDetErro] = React.useState(null);
  const [detCarregando, setDetCarregando] = React.useState(false);
  const [giro, setGiro] = React.useState(0);            // "Atualizar" tambem recarrega Detalhes
  const [alvo, setAlvo] = React.useState(null);
  const [avisoPessoas, setAvisoPessoas] = React.useState('');
  const [voltarPara, setVoltarPara] = React.useState(null);
  const seqBase = React.useRef(0);
  const seqDet = React.useRef(0);
  const raizRef = React.useRef(null);
  const rolarRef = React.useRef(null);
  const passouPromocoes = React.useRef(false);

  React.useEffect(() => { gravarPrefs({ dias, secao }); }, [dias, secao]);

  const carregar = React.useCallback(() => {
    const minha = ++seqBase.current;
    setCarregando(true);
    const pegar = f => Promise.resolve().then(f).catch(e => ({ error: e }));
    Promise.all([
      pegar(() => supabase.rpc('central_uso', { p_dias: 30, p_incluir_internos: false })),
      pegar(() => supabase.rpc('metricas_piloto')),
      pegar(() => supabase.from('recompensas').select('valor, professor_id').eq('status', 'ganha')),
      pegar(() => supabase.rpc('painel_professores', { p_incluir_internos: false })),
    ]).then(([r1, r2, r3, r4]) => {
      if (minha !== seqBase.current) return;
      setCarregando(false);
      // central_uso falhou: fica tudo como estava (base, piloto, premios e extra do mesmo
      // momento), so' com o aviso — senao a tela mistura numeros velhos com "—"
      if (!r1 || r1.error) { setErro(classificarErro((r1 && r1.error) || 'sem resposta')); return; }
      setBase(normalizar(r1.data)); setErro(null);
      setPiloto(r2 && !r2.error ? lerJson(r2.data) : null);
      setPremios(r3 && !r3.error ? arr(r3.data) : []);
      setExtraBase(lerExtra(r4));
    });
    setGiro(g => g + 1);
  }, [supabase]);
  // so' os premios (volta da Promocoes: o que foi pago some do Inicio sem recarregar o resto)
  const recarregarPremios = React.useCallback(() => {
    Promise.resolve().then(() => supabase.from('recompensas').select('valor, professor_id').eq('status', 'ganha'))
      .then(r => { if (r && !r.error) setPremios(arr(r.data)); }, () => {});
  }, [supabase]);
  React.useEffect(() => { carregar(); }, [carregar]);

  // volta automatica: a aba voltou a ficar visivel e os numeros tem mais de 15 minutos
  React.useEffect(() => {
    const aoVer = () => {
      try {
        if (document.visibilityState !== 'visible' || !base) return;
        const g = dataValida(base.gerado_em);
        if (g && Date.now() - g.getTime() > 15 * 60000) carregar();
      } catch (e) {}
    };
    try { document.addEventListener('visibilitychange', aoVer); } catch (e) {}
    return () => { try { document.removeEventListener('visibilitychange', aoVer); } catch (e) {} };
  }, [base, carregar]);

  // Detalhes so' busca de novo quando o periodo (nos assuntos que tem periodo) ou as contas de
  // teste mudam o que o Inicio ja' carregou; senao, usa os mesmos numeros do Inicio.
  const sec = SECOES.find(s => s.k === secao) || SECOES[0];
  const pode90 = !(base && base.crescimento.dias_medidos != null && base.crescimento.dias_medidos < 30);
  const diasSel = dias === 90 && !pode90 ? 30 : dias;
  const diasEf = sec.periodo ? diasSel : 30;
  const precisaDet = vista === 'detalhes' && !!base && (diasEf !== 30 || internos);
  const chaveDet = diasEf + ':' + internos;
  React.useEffect(() => {
    if (!precisaDet) return;
    const minha = ++seqDet.current;
    setDetCarregando(true);
    setDetErro(null);   // carga nova: some o "Sem conexao" antigo e aparece "Atualizando…"
    const pegar = f => Promise.resolve().then(f).catch(e => ({ error: e }));
    Promise.all([
      pegar(() => supabase.rpc('central_uso', { p_dias: diasEf, p_incluir_internos: internos })),
      internos ? pegar(() => supabase.rpc('painel_professores', { p_incluir_internos: true })) : Promise.resolve(null),
    ]).then(([r1, r2]) => {
      if (minha !== seqDet.current) return;
      if (!r1 || r1.error) setDetErro({ chave: chaveDet, e: classificarErro((r1 && r1.error) || 'sem resposta') });
      else { setDet({ chave: chaveDet, dados: normalizar(r1.data), extra: lerExtra(r2) }); setDetErro(null); }
      setDetCarregando(false);
    });
  }, [precisaDet, chaveDet, giro]);
  // Enquanto a carga nova nao chega, Detalhes continua com os numeros que ja' tinha (e mostra
  // "Atualizando…"): o assunto nao desmonta, entao a ficha aberta e os filtros de Pessoas ficam
  // como estavam. Se a carga falhar, os numeros somem e entra a caixa de erro com "Tentar de novo".
  const detErroAtual = detErro && detErro.chave === chaveDet ? detErro.e : null;
  const detOk = !!(det && det.chave === chaveDet);
  const detAntes = precisaDet && !detOk && !detErroAtual;   // carregando (ou a ponto de carregar)
  const dDet = !precisaDet ? base : detOk ? det.dados : detAntes ? (det ? det.dados : base) : null;
  const extraDet = !precisaDet ? extraBase : detOk ? (internos ? det.extra : extraBase) : detAntes ? (det && det.dados.incluiu_internos ? det.extra : extraBase) : null;
  const versaoAr = base ? versaoDoPainel(base, versaoNoAr) : versaoNoAr;

  // onAba: so' quando a vista ou o assunto muda (nao na montagem)
  const chaveVista = vista === 'inicio' ? 'inicio' : 'detalhes:' + secao;
  const ultimaVista = React.useRef(chaveVista);
  React.useEffect(() => {
    if (chaveVista === 'detalhes:promocoes') passouPromocoes.current = true;
    if (ultimaVista.current === chaveVista) return;
    ultimaVista.current = chaveVista;
    if (typeof onAba === 'function') { try { onAba(vista === 'inicio' ? 'inicio' : secao === 'pessoas' ? 'pessoas' : 'detalhes'); } catch (e) {} }
  }, [chaveVista]);

  // rolagem depois de trocar de vista: Detalhes abre no topo; "← Inicio" volta a' lista
  // "topo" so' rola quando o comeco do painel esta' escondido: acima da janela ou, dentro do app
  // no celular, embaixo da barra fixa do topo (a margem vem do scroll-margin-top do CSS).
  React.useEffect(() => {
    const alvoRol = rolarRef.current;
    rolarRef.current = null;
    if (!alvoRol) return;
    try {
      if (alvoRol === 'topo') {
        const el = raizRef.current;
        if (!el) return;
        const caixa = el.closest ? el.closest('.app-main') : null;
        const util = (caixa ? caixa.getBoundingClientRect().top : 0) + (parseFloat(getComputedStyle(el).scrollMarginTop) || 0);
        if (el.getBoundingClientRect().top < util) el.scrollIntoView({ block: 'start' });
      } else {
        const el = document.getElementById(alvoRol);
        if (el) el.scrollIntoView({ block: 'start' });
      }
    } catch (e) {}
  }, [vista, secao, alvo]);

  const irPara = (k, opcoes) => {
    rolarRef.current = 'topo';
    setSecao(k);
    setVista('detalhes');
    if (k !== 'pessoas') setAvisoPessoas('');
    setAlvo(opcoes ? Object.assign({ t: Date.now() }, opcoes) : null);
    setVoltarPara(opcoes && opcoes.de === 'inicio' ? 'pc-professores' : null);
  };
  const abrirPessoa = (uid, extra) => irPara('pessoas', Object.assign({ user_id: uid }, extra || {}));
  const voltarInicio = () => {
    // passou pela Promocoes (pode ter marcado premio como pago): a frase de premio se atualiza
    if (passouPromocoes.current) { passouPromocoes.current = false; recarregarPremios(); }
    rolarRef.current = voltarPara || 'topo'; setVista('inicio'); setVoltarPara(null);
  };
  const alvoUsado = React.useCallback(() => setAlvo(null), []);
  const verContasTeste = () => { setInternos(true); irPara('pessoas'); };
  const aoMudarPessoa = texto => { setAvisoPessoas(texto); carregar(); };

  let corpo;
  if (!base && erro && !carregando) {
    corpo = <CaixaErro erro={erro} onTentar={carregar} />;
  } else if (!base) {
    corpo = (
      <>
        <LinhaTopo vista="inicio" gerado={null} carregando onAtualizar={carregar} />
        <EsqueletoInicio />
      </>
    );
  } else {
    corpo = (
      <>
        <LinhaTopo vista={vista} gerado={base.gerado_em} carregando={carregando} onAtualizar={carregar}
          onDetalhes={() => irPara(secao)} onInicio={voltarInicio} />
        {erro ? <Aviso cor="ambar" icone="alerta">Não deu para atualizar agora. Mostrando os números de {fmtHora(base.gerado_em)}.</Aviso> : null}
        {vista === 'inicio' ? (
          <AbaInicio base={base} piloto={piloto} premios={premios} extra={extraBase} versaoNoAr={versaoAr} email={email}
            irPara={irPara} abrirPessoa={abrirPessoa} verContasTeste={verContasTeste} supabase={supabase} giro={giro} />
        ) : (
          <VistaDetalhes secao={sec.k} irPara={irPara} d={dDet} carregando={precisaDet ? detCarregando || detAntes : carregando}
            erro={precisaDet ? detErroAtual : null} onTentar={() => setGiro(g => g + 1)} piloto={piloto} extra={extraDet}
            dias={diasSel} setDias={setDias} pode90={pode90} internos={internos} setInternos={setInternos}
            supabase={supabase} alvo={alvo} onMudouPessoa={aoMudarPessoa} avisoPessoas={avisoPessoas}
            promocoes={promocoes} versaoNoAr={versaoAr} abrirPessoa={abrirPessoa} onAlvoUsado={alvoUsado} />
        )}
      </>
    );
  }
  return (
    <div ref={raizRef} className="pc-raiz" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{CSS}</style>
      {corpo}
    </div>
  );
}

// So' para o teste de render (harness): funcoes puras e pecas que recebem dados prontos.
export const PARA_TESTE = { normalizar, montarCarteira, saudeDe, semanasRef, estadoPlen, fmtR, fmtRq, compara, linkWhats, rotMotivo, versaoDoPainel, ultimoUsoTxt, agrupar, AbaInicio, VistaDetalhes, EsqueletoInicio, ProfessoresDetalhe, TabelaSemanas, BlocoNoPlen, AbaCrescimento, AbaFunis, BlocoIndicacoes };

export default PainelCentral;
